import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useOffline } from '../../contexts/OfflineContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  addDiverseOrderItem,
  authorizeGpTomPayment,
  authorizeSumUpPayment,
  cancelOrder,
  completeOrder,
  createOrder,
  discountOrderItem,
  getArticleOptionGroups,
  getCategories,
  getGpTomTaskStatus,
  getOrders,
  getPosLayoutConfig,
  getPosSettingsPayload,
  getProducts,
  getSumUpTransactionStatus,
  getTables,
  sendOrderCourse,
  splitOrderForPayment,
  updateOrderItems,
  voidOrderItem,
} from '../../lib/api';
import { buildCatalogViewFromMenu, buildEmptySalesCatalog, getMenusForScope, normalizeSalesCatalog, scopeFromFulfillment } from '../../lib/salesCatalog';
import {
  POS_DEFAULT_TERMINAL_THEME,
  findDefaultCategoryAppearance,
  normalizeCategoryAppearanceName,
} from '../../lib/posCategoryAppearance';
import { getEnabledPaymentMethods, getPaymentMethodLabel, isGpTomConfigured, isSumUpConfigured } from '../../lib/paymentMethods';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '../../components/ui/drawer';
import {
  Beef, Beer, BookOpen, Coffee, ConciergeBell, CookingPot, Croissant, CupSoda, Fish, GlassWater,
  IceCreamCone, LayoutGrid, LeafyGreen, Martini, Package, Pizza, Salad, Sandwich, Soup, Sparkles,
  Star, Ticket, TicketPercent, UsersRound, Wine,
  Plus, Minus, Trash2, CreditCard, Banknote, Loader2, Percent, X,
  ShoppingCart, CheckCircle2, XCircle, Wifi, Smartphone,
  RotateCcw, ChevronLeft, ClipboardList, UtensilsCrossed, Search, Delete, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { useSwipeable } from 'react-swipeable';
import ReceiptDialog from '../../components/ReceiptDialog';
import StornoDialog, { STORNO_REASON_LABELS } from '../../components/StornoDialog';
import RabattDialog from '../../components/RabattDialog';
import DiverseBuchungDialog from '../../components/DiverseBuchungDialog';

const GASTRONOVI_CARD = '#ffffff';
const GASTRONOVI_BORDER = '#e2e8f0';

const categoryIcons = {
  GlassWater,
  Soup,
  UtensilsCrossed,
  IceCream: IceCreamCone,
  ClipboardList,
  Package,
  Pizza,
  Salad,
  Sandwich,
  Beer,
  Wine,
  Martini,
  Coffee,
  Ticket,
  Fish,
  Beef,
  LeafyGreen,
  CookingPot,
  CupSoda,
  Croissant,
  BookOpen,
  ConciergeBell,
  LayoutGrid,
  Sparkles,
  Star,
  TicketPercent,
  UsersRound,
};

const GENERIC_CATEGORY_ICON_KEYS = new Set(['', 'Package', 'ClipboardList', 'LayoutGrid']);

function resolveCategoryIconComponent(iconKey) {
  return categoryIcons[String(iconKey || '').trim()] || null;
}

const PRODUCT_COURSE_VISUALS = {
  starter: {
    key: 'starter',
    label: 'Vorspeise',
    icon: Sandwich,
    accent: '#748da3',
    chipBg: '#edf2f6',
  },
  main: {
    key: 'main',
    label: 'Hauptgang',
    icon: CookingPot,
    accent: '#7c9448',
    chipBg: '#eef4df',
  },
  dessert: {
    key: 'dessert',
    label: 'Dessert',
    icon: IceCreamCone,
    accent: '#8f5ad7',
    chipBg: '#f1e9fd',
  },
  drink: {
    key: 'drink',
    label: 'Getraenk',
    icon: CupSoda,
    accent: '#5d99bc',
    chipBg: '#e8f3f8',
  },
  default: {
    key: 'default',
    label: 'Artikel',
    icon: UtensilsCrossed,
    accent: '#74808b',
    chipBg: '#eef2f4',
  },
};

const FALLBACK_PRODUCT_VISUAL = {
  icon: UtensilsCrossed,
  iconBg: PRODUCT_COURSE_VISUALS.default.accent,
  accent: PRODUCT_COURSE_VISUALS.default.accent,
  border: 'rgba(116, 128, 139, 0.28)',
  borderStrong: 'rgba(116, 128, 139, 0.46)',
  priceBg: 'rgba(116, 128, 139, 0.12)',
  priceText: '#24323b',
  courseIcon: PRODUCT_COURSE_VISUALS.default.icon,
  courseLabel: PRODUCT_COURSE_VISUALS.default.label,
  courseChipBg: PRODUCT_COURSE_VISUALS.default.chipBg,
  showCourse: false,
  categoryName: '',
};

function normalizeCourseKey(value) {
  const normalized = normalizeCategoryAppearanceName(value);
  if (!normalized) return '';

  if (['vorspeise', 'starter', 'appetizer', 'antipasti'].some((keyword) => normalized.includes(keyword))) {
    return 'starter';
  }
  if (['hauptgang', 'hauptspeise', 'main course', 'main'].some((keyword) => normalized.includes(keyword))) {
    return 'main';
  }
  if (['nachspeise', 'dessert', 'dolci', 'sweet'].some((keyword) => normalized.includes(keyword))) {
    return 'dessert';
  }
  if (['getrank', 'getraenk', 'drink', 'beverage'].some((keyword) => normalized.includes(keyword))) {
    return 'drink';
  }

  return '';
}

function inferCourseKeyFromCategory(category) {
  const normalized = normalizeCategoryAppearanceName(category?.name);
  if (!normalized) return '';

  if (['antipast', 'vorspeise', 'starter', 'zuppe', 'suppe'].some((keyword) => normalized.includes(keyword))) {
    return 'starter';
  }
  if (['dolci', 'dessert', 'nachspeise', 'kuchen'].some((keyword) => normalized.includes(keyword))) {
    return 'dessert';
  }
  if (
    ['bibite', 'getrank', 'getraenk', 'bar', 'wein', 'bier', 'cocktail', 'spritz', 'kaffee', 'coffee', 'tee'].some(
      (keyword) => normalized.includes(keyword),
    )
  ) {
    return 'drink';
  }
  if (normalized) return 'main';

  return '';
}

function buildProductVisual(product, category, tone, articleCourses = {}) {
  const resolvedTone = tone || getCategoryVisual(category);
  const configuredCourseKey = normalizeCourseKey(articleCourses?.[product?.id]);
  const inferredCourseKey = inferCourseKeyFromCategory(category);
  const courseKey = configuredCourseKey || inferredCourseKey;
  const course = PRODUCT_COURSE_VISUALS[courseKey] || PRODUCT_COURSE_VISUALS.default;
  // Category color is the visual anchor in every catalog level; course only supplies its label and icon.
  const accent = resolvedTone?.base || (courseKey ? course.accent : PRODUCT_COURSE_VISUALS.default.accent);

  return {
    // The product tile represents its category; the smaller course label keeps the course icon.
    icon: resolvedTone?.icon || (courseKey ? course.icon : PRODUCT_COURSE_VISUALS.default.icon),
    iconBg: accent,
    accent,
    border: withAlpha(accent, 0.28),
    borderStrong: withAlpha(accent, 0.46),
    priceBg: withAlpha(accent, 0.12),
    priceText: '#24323b',
    courseIcon: course.icon,
    courseLabel: course.label,
    courseChipBg: course.chipBg,
    showCourse: Boolean(courseKey),
    categoryName: category?.name || '',
  };
}

// Quick cash amounts for the numpad-style buttons
const QUICK_CASH = [5, 10, 20, 50, 100];
const QUICK_TIPS  = [0, 0.5, 1, 2, 5];

// Order panel (Bestellschein) width, configurable per company from the manager portal
const ORDER_PANEL_WIDTH_CLASSES = {
  normal: 'lg:w-[21rem] xl:w-[24rem]',
  wide:   'lg:w-[26rem] xl:w-[32rem]',
  xwide:  'lg:w-[32rem] xl:w-[40rem]',
};

const TERMINAL_POLL_INTERVAL_MS = 2000;
const TERMINAL_POLL_TIMEOUT_MS = 65000;
const INTEGRATED_CARD_METHOD_IDS = ['card', 'card_sumup'];

function isIntegratedCardMethodId(methodId) {
  return INTEGRATED_CARD_METHOD_IDS.includes(String(methodId || '').trim().toLowerCase());
}

function isSumUpMethodId(methodId) {
  return String(methodId || '').trim().toLowerCase() === 'card_sumup';
}

function hexToRgb(color) {
  const safe = String(color || '').trim();
  if (!safe.startsWith('#')) return null;

  let value = safe.slice(1);
  if (value.length === 3) {
    value = value.split('').map((part) => part + part).join('');
  }
  if (value.length !== 6) return null;

  const parsed = Number.parseInt(value, 16);
  if (Number.isNaN(parsed)) return null;

  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255,
  };
}

function withAlpha(color, alpha) {
  const rgb = hexToRgb(color);
  if (!rgb) return color || '#475569';
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

function mixColors(baseColor, targetColor, weight = 0.5) {
  const base = hexToRgb(baseColor);
  const target = hexToRgb(targetColor);
  if (!base || !target) return baseColor || targetColor;

  const mix = (from, to) => Math.round(from * (1 - weight) + to * weight);
  return `rgb(${mix(base.r, target.r)}, ${mix(base.g, target.g)}, ${mix(base.b, target.b)})`;
}

function resolveCategoryAppearance(category, options = {}) {
  const appearanceById =
    options?.appearanceById && typeof options.appearanceById === 'object'
      ? options.appearanceById
      : {};
  const appearanceByName =
    options?.appearanceByName && typeof options.appearanceByName === 'object'
      ? options.appearanceByName
      : {};
  const explicitKey = String(category?.icon || '').trim();
  const normalizedName = normalizeCategoryAppearanceName(category?.name);
  const configuredById =
    category?.id && appearanceById[category.id] && typeof appearanceById[category.id] === 'object'
      ? appearanceById[category.id]
      : null;
  const configuredByName =
    normalizedName && appearanceByName[normalizedName] && typeof appearanceByName[normalizedName] === 'object'
      ? appearanceByName[normalizedName]
      : null;
  const configured = configuredById || configuredByName || {};
  const defaultPreset = findDefaultCategoryAppearance(category?.name) || {};

  const iconKey = String(
    configured.icon ||
      (!GENERIC_CATEGORY_ICON_KEYS.has(explicitKey) ? explicitKey : '') ||
      defaultPreset.icon ||
      explicitKey ||
      'UtensilsCrossed',
  ).trim();
  const icon = resolveCategoryIconComponent(iconKey) || UtensilsCrossed;

  return {
    icon,
    iconKey,
    color: String(configured.color || category?.color || defaultPreset.color || '#b7bcc2'),
    backgroundColor: String(configured.backgroundColor || defaultPreset.backgroundColor || ''),
  };
}

function getCategoryVisual(category, options = {}) {
  const theme = {
    ...POS_DEFAULT_TERMINAL_THEME,
    ...(options?.theme || {}),
  };
  const resolved = resolveCategoryAppearance(category, options);
  const base = resolved.color || '#b9c0c3';
  const soft = resolved.backgroundColor || mixColors(base, theme.canvasColor || GASTRONOVI_CARD, 0.68);
  const softer = resolved.backgroundColor || mixColors(base, theme.railColor || GASTRONOVI_CARD, 0.8);
  return {
    base,
    soft,
    softer,
    border: withAlpha(base, 0.32),
    borderStrong: withAlpha(base, 0.52),
    iconBg: base,
    icon: resolved.icon,
    iconKey: resolved.iconKey,
  };
}

function CategoryFeatureCard({ category, onClick, tone, subtitle = '' }) {
  const Icon = tone.icon;
  const badgeLabel = subtitle || 'Kategorie';

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex min-h-[84px] overflow-hidden border text-left transition-colors hover:bg-card"
      style={{ backgroundColor: tone.soft, borderColor: tone.borderStrong }}
    >
      <div className="w-1 shrink-0" style={{ backgroundColor: tone.base }} />
      <div className="flex min-w-0 flex-1 flex-col justify-between gap-1.5 px-2.5 py-2.5">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center" style={{ color: tone.base }}>
            <Icon className="h-[18px] w-[18px]" strokeWidth={2.1} />
          </span>
          <p className="line-clamp-2 text-[14px] font-semibold leading-tight text-[#24323b] sm:text-[15px]">{category.name}</p>
        </div>

        <p className="text-[9px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
          {subtitle ? badgeLabel : 'Oeffnen'}
        </p>
      </div>
    </button>
  );
}

function ProductFeatureCard({ product, onClick, showCategoryLabel = false, visual }) {
  const safeVisual = visual && typeof visual === 'object'
    ? { ...FALLBACK_PRODUCT_VISUAL, ...visual }
    : FALLBACK_PRODUCT_VISUAL;
  const Icon = safeVisual.icon || FALLBACK_PRODUCT_VISUAL.icon;
  const CourseIcon = safeVisual.courseIcon || FALLBACK_PRODUCT_VISUAL.courseIcon;
  const showCategoryChip = showCategoryLabel && safeVisual.categoryName;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex min-h-[108px] items-stretch overflow-hidden border bg-card text-left transition-colors hover:bg-muted active:bg-border"
      style={{ borderColor: safeVisual.borderStrong || GASTRONOVI_BORDER }}
      data-testid={`prod-${product.id}`}
    >
      <div className="w-1 shrink-0" style={{ backgroundColor: safeVisual.accent }} />
      <div className="flex min-w-0 flex-1 flex-col px-2.5 py-2.5">
        <div className="flex items-start gap-2">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center" style={{ color: safeVisual.accent }}>
            <Icon className="h-[18px] w-[18px]" strokeWidth={2.1} />
          </span>
          <p className="line-clamp-2 pt-0.5 text-[15px] font-semibold leading-tight text-[#24323b] sm:text-base">{product.name}</p>
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          {safeVisual.showCourse ? (
            <span className="inline-flex items-center gap-1 text-[8px] font-semibold uppercase tracking-[0.08em] text-[#526068]">
              <CourseIcon className="h-2.5 w-2.5" style={{ color: safeVisual.accent }} strokeWidth={2.1} />
              {safeVisual.courseLabel}
            </span>
          ) : null}

          {showCategoryChip ? (
            <span className="text-[8px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {safeVisual.categoryName}
            </span>
          ) : null}
        </div>

        <span className="mt-auto self-end text-[13px] font-bold tabular-nums text-[#24323b] sm:text-[14px]">
          €{product.price.toFixed(2)}
        </span>
      </div>
    </button>
  );
}

// ─── Card terminal status flow ────────────────────────────────────────────────
function CardTerminalStatus({ status, total, errorMessage, onRetry, onCancel }) {
  const steps = {
    connecting:  { label: 'Verbinde mit Terminal…',        icon: Wifi,          spin: true,  color: 'text-muted-foreground' },
    waiting:     { label: 'Bitte Karte einlesen oder\nGerät halten (NFC)',   icon: Smartphone,    spin: false, color: 'text-blue-600' },
    processing:  { label: 'Transaktion wird verarbeitet…', icon: Loader2,       spin: true,  color: 'text-amber-600' },
    success:     { label: 'Zahlung erfolgreich!',          icon: CheckCircle2,  spin: false, color: 'text-emerald-600' },
    error:       { label: 'Transaktion fehlgeschlagen.',   icon: XCircle,       spin: false, color: 'text-red-600' },
  };
  const s = steps[status] || steps.waiting;
  const Icon = s.icon;

  return (
    <div className="flex flex-col items-center gap-4 py-4">
      <div className={`flex h-24 w-20 flex-col items-center justify-center gap-2 border-2
        ${status === 'success' ? 'border-emerald-500 bg-emerald-950/30' :
          status === 'error'   ? 'border-red-500 bg-red-950/30' :
                                 'border-slate-700 bg-[#101722]'}`}>
        <div className="flex h-7 w-12 items-center justify-center bg-slate-800">
          <div className={`h-1.5 w-6 ${
            status === 'waiting' ? 'bg-blue-500 animate-pulse' : 'bg-slate-400'}`} />
        </div>
        <div className="flex gap-1">
          {[0,1,2].map(i => (
            <div key={i} className={`h-2 w-2 ${
              status === 'success' ? 'bg-emerald-500' :
              status === 'error'   ? 'bg-red-500' :
                                     'bg-slate-300'}`} />
          ))}
        </div>
      </div>

      <div className="space-y-1 text-center">
        <Icon className={`mx-auto h-8 w-8 ${s.color} ${s.spin ? 'animate-spin' : ''}`} />
        <p className="whitespace-pre-line text-sm font-medium text-slate-200">{s.label}</p>
        {status === 'error' && errorMessage ? (
          <p className="mx-auto max-w-[18rem] text-xs text-red-300">{errorMessage}</p>
        ) : null}
      </div>

      <div className="text-3xl font-bold text-white tabular-nums">
        €{total.toFixed(2)}
      </div>

      {status === 'error' && (
        <div className="flex w-full gap-2">
          <Button variant="outline" className="flex-1 rounded-none border-slate-700 bg-[#101722] text-slate-100 hover:bg-[#16202c]" onClick={onCancel}>
            <ChevronLeft className="mr-1 h-4 w-4" /> Zurueck
          </Button>
          <Button className="flex-1 rounded-none bg-[#18323b] hover:bg-[#234450]" onClick={onRetry}>
            <RotateCcw className="mr-1 h-4 w-4" /> Nochmal
          </Button>
        </div>
      )}
      {(status === 'connecting' || status === 'waiting' || status === 'processing') && (
        <Button variant="outline" size="sm" className="rounded-none border-slate-700 bg-[#101722] text-slate-300 hover:bg-[#16202c]" onClick={onCancel}>
          Abbrechen
        </Button>
      )}
    </div>
  );
}

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function buildSplitUnits(items) {
  return (items || []).flatMap((item, itemIndex) => {
    const quantity = Math.max(1, Number(item.quantity) || 1);
    const baseKey = item.cartKey || item.product_id || `line-${itemIndex}`;
    return Array.from({ length: quantity }, (_, unitIndex) => ({
      unitId: `${baseKey}::${unitIndex}`,
      cartKey: baseKey,
      product_id: item.product_id,
      product_name: item.product_name,
      price: Number(item.price) || 0,
      vat_rate: Number(item.vat_rate) || 10,
      total: Number(item.price) || 0,
      modifiers: item.modifiers || [],
      print_group: item.print_group || '',
      notes: item.notes || '',
      status: item.status || 'pending',
      menu_id: item.menu_id || '',
      menu_item_id: item.menu_item_id || '',
      menu_group_id: item.menu_group_id || '',
      isSent: Boolean(item.isSent),
    }));
  });
}

function buildSplitSelection(units, selectedMap) {
  const selectedUnits = (units || []).filter((unit) => selectedMap[unit.unitId]);
  const grouped = new Map();

  selectedUnits.forEach((unit) => {
    const existing = grouped.get(unit.cartKey);
    if (existing) {
      existing.quantity += 1;
      existing.total = roundMoney(existing.quantity * existing.price);
      return;
    }
    grouped.set(unit.cartKey, {
      ...unit,
      quantity: 1,
      total: roundMoney(unit.price),
      cartKey: unit.cartKey,
    });
  });

  const items = Array.from(grouped.values());
  return {
    items,
    selections: items.map((item) => ({ cart_key: item.cartKey, quantity: item.quantity })),
    total: roundMoney(selectedUnits.reduce((sum, unit) => sum + (Number(unit.price) || 0), 0)),
    selectedCount: selectedUnits.length,
    isAllSelected: selectedUnits.length > 0 && selectedUnits.length === (units || []).length,
  };
}

// ─── Payment Dialog ────────────────────────────────────────────────────────────
function PaymentDialog({
  open,
  onClose,
  finalTotal,
  tip,
  setTip,
  onPay,
  isProcessing,
  defaultMethod,
  paymentMethods,
  gpTomConfigured,
  sumUpConfigured,
  isOnline,
  items,
  allowSplitPayments,
  initialSplitMode = 'shared',
  receiptEnabled = true,
  onReceiptEnabledChange = () => {},
}) {
  const [method, setMethod] = useState(null); // null | payment method id
  const [cashGiven, setCashGiven] = useState('');
  const [cardStatus, setCardStatus] = useState('idle'); // idle | connecting | waiting | processing | success | error
  const [cardErrorMessage, setCardErrorMessage] = useState('');
  const [splitMode, setSplitMode] = useState('shared'); // shared | split
  const [splitStep, setSplitStep] = useState('payment'); // selection | payment
  const [selectedSplitUnits, setSelectedSplitUnits] = useState({});

  const splitUnits = buildSplitUnits(items);
  const splitSelection = buildSplitSelection(splitUnits, selectedSplitUnits);
  const enabledMethods = paymentMethods || [];
  const integratedCardMethods = enabledMethods.filter((entry) => isIntegratedCardMethodId(entry.id));
  const selectedIntegratedMethod = integratedCardMethods.find((entry) => entry.id === method) || null;
  const baseTotal = splitMode === 'split' ? splitSelection.total : finalTotal;
  const paymentSelectionMissing = splitMode === 'split' && splitSelection.selectedCount === 0;
  const isChoosingSplit = splitMode === 'split' && splitStep === 'selection';
  const tipLocked = method === 'internal';
  const appliedTip = tipLocked ? 0 : (paymentSelectionMissing ? 0 : tip);
  const totalWithTip = roundMoney(baseTotal + appliedTip);
  const cashNum = parseFloat(cashGiven) || 0;
  const change = roundMoney(Math.max(0, cashNum - totalWithTip));
  const totalUnitCount = splitUnits.length || items.length;
  const selectedItemCount = splitMode === 'split' ? splitSelection.selectedCount : totalUnitCount;
  const interactionLocked = isProcessing || (isIntegratedCardMethodId(method) && ['connecting', 'waiting', 'processing'].includes(cardStatus));

  const selectAllSplitUnits = () => {
    setSelectedSplitUnits(Object.fromEntries(splitUnits.map((unit) => [unit.unitId, true])));
  };

  const getIntegratedMethodAvailability = (entry) => {
    if (!isOnline) {
      return {
        available: false,
        message: `${entry.name} ist nur online verfuegbar.`,
      };
    }
    if (entry.id === 'card') {
      return {
        available: gpTomConfigured,
        message: 'GP tom bitte zuerst in den Zahlungsarten mit Cloud API Key, Benutzername, Passwort und TID konfigurieren.',
      };
    }
    if (entry.id === 'card_sumup') {
      return {
        available: sumUpConfigured,
        message: 'SumUp bitte zuerst mit Secret API Key, Merchant Code, Affiliate Key, Affiliate App ID und Reader ID konfigurieren.',
      };
    }
    return {
      available: false,
      message: `${entry.name} ist noch nicht verfuegbar.`,
    };
  };

  const integratedMethodWarnings = integratedCardMethods
    .map((entry) => getIntegratedMethodAvailability(entry))
    .filter((entry) => !entry.available)
    .map((entry) => entry.message);
  const receiptHint = receiptEnabled
    ? 'Nach erfolgreicher Zahlung oeffnen wir den Beleg zum Drucken oder Senden.'
    : 'Der Beleg wird gespeichert, aber nicht automatisch geoeffnet.';

  const buildPaymentContext = (overrides = {}) => ({
    ...overrides,
    receiptEnabled,
    splitSelection: splitMode === 'split'
      ? {
        mode: 'separate',
        ...splitSelection,
      }
      : {
        mode: 'shared',
        items,
        selections: [],
        total: finalTotal,
        selectedCount: 0,
        isAllSelected: true,
      },
  });

  const resetDialog = () => {
    setMethod(null);
    setCashGiven('');
    setTip(0);
    setCardStatus('idle');
    setCardErrorMessage('');
    const nextSplitMode = initialSplitMode === 'split' && allowSplitPayments ? 'split' : 'shared';
    setSplitMode(nextSplitMode);
    setSplitStep(nextSplitMode === 'split' ? 'selection' : 'payment');
    setSelectedSplitUnits(nextSplitMode === 'split'
      ? {}
      : {});
  };

  const handleClose = () => {
    resetDialog();
    onClose();
  };

  const clearSelectedMethod = () => {
    setMethod(null);
    setCashGiven('');
    setCardStatus('idle');
    setCardErrorMessage('');
  };

  const confirmCash = async () => {
    try {
      await onPay('cash', appliedTip, null, buildPaymentContext());
      handleClose();
    } catch {
      // Error state is surfaced by onPay.
    }
  };

  const confirmManualCard = async () => {
    try {
      await onPay('card_manual', appliedTip, null, buildPaymentContext({ provider: 'manual_terminal' }));
      handleClose();
    } catch {
      // Error state is surfaced by onPay.
    }
  };

  const confirmBankTransfer = async () => {
    try {
      await onPay('bank_transfer', appliedTip, null, buildPaymentContext({ provider: 'bank_transfer' }));
      handleClose();
    } catch {
      // Error state is surfaced by onPay.
    }
  };

  const confirmCustomerCard = async () => {
    try {
      await onPay('customer_card', appliedTip, null, buildPaymentContext({
        provider: 'customer_card',
        paymentMetadata: { booking_type: 'customer_card' },
      }));
      handleClose();
    } catch {
      // Error state is surfaced by onPay.
    }
  };

  const confirmInternalBooking = async () => {
    try {
      await onPay('internal', 0, 'eigenverbrauch', buildPaymentContext({
        provider: 'internal',
        paymentMetadata: { internal_booking_type: 'eigenverbrauch' },
      }));
      handleClose();
    } catch {
      // Error state is surfaced by onPay.
    }
  };

  const startIntegratedCardPayment = async (methodId) => {
    const selectedMethod = integratedCardMethods.find((entry) => entry.id === methodId);
    const availability = getIntegratedMethodAvailability(selectedMethod || {});

    if (!selectedMethod || !availability.available) {
      setCardStatus('error');
      setCardErrorMessage(availability.message || 'Kartenzahlung ist gerade nicht verfuegbar.');
      return;
    }

    setCardErrorMessage('');
    setCardStatus('connecting');

    try {
      await onPay(selectedMethod.id, appliedTip, null, buildPaymentContext({
        provider: selectedMethod.provider,
        onStatusChange: setCardStatus,
      }));
      handleClose();
    } catch (error) {
      setCardStatus('error');
      setCardErrorMessage(getApiErrorMessage(error, 'Kartenzahlung fehlgeschlagen.'));
    }
  };

  const selectMethod = (methodId) => {
    if (isChoosingSplit || paymentSelectionMissing) {
      toast.warning('Bitte zuerst Artikel auswaehlen und weiter zur Zahlart gehen.');
      return;
    }

    if (methodId !== 'cash') {
      setCashGiven('');
    }
    if (methodId === 'internal') {
      setTip(0);
    }

    setCardStatus('idle');
    setCardErrorMessage('');
    setMethod(methodId);

    if (isIntegratedCardMethodId(methodId)) {
      startIntegratedCardPayment(methodId);
    }
  };

  const renderActionPanel = () => {
    if (isChoosingSplit || paymentSelectionMissing) {
      return (
        <div className="border border-slate-700 bg-[#16202c] px-3 py-4">
          <p className="text-sm text-slate-300">Artikel fuer diese Teilzahlung markieren und danach die Zahlart waehlen.</p>
          <Button
            type="button"
            onClick={() => {
              if (paymentSelectionMissing) {
                toast.warning('Bitte mindestens einen Artikel markieren.');
                return;
              }
              setSplitStep('payment');
            }}
            disabled={interactionLocked}
            className="mt-3 h-11 w-full rounded-none bg-emerald-600 text-base font-black hover:bg-emerald-500"
          >
            Weiter zur Zahlart
          </Button>
        </div>
      );
    }

    if (!method) {
      return (
        <div className="border border-dashed border-slate-700 bg-[#16202c] px-3 py-4 text-sm text-slate-300">
          Zahlart direkt antippen. Der komplette Flow bleibt in diesem einen Popup bis zum Beleg.
        </div>
      );
    }

    if (method === 'cash') {
      return (
        <div className="border border-slate-700 bg-[#16202c]">
          <div className="border-b border-slate-700 bg-[#101722] px-3 py-3">
            <div className="flex items-end justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Barzahlung</p>
                <p className="mt-1 text-sm text-slate-300">Bargeld eingeben und Wechselgeld sofort sehen.</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Zu zahlen</p>
                <p className="text-3xl font-black tabular-nums text-white">€{totalWithTip.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="space-y-3 p-3">
            <div className="grid grid-cols-5 gap-px border border-slate-700 bg-slate-800">
              {QUICK_CASH.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => setCashGiven(String(Math.ceil(totalWithTip / amount) * amount))}
                  className="h-11 bg-[#101722] text-sm font-semibold text-white transition-colors hover:bg-[#1b2633]"
                  disabled={interactionLocked}
                >
                  €{amount}
                </button>
              ))}
            </div>

            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_10rem]">
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Erhalten</p>
                <Input
                  type="number"
                  step="0.01"
                  min={totalWithTip}
                  placeholder={`Min. €${totalWithTip.toFixed(2)}`}
                  value={cashGiven}
                  onChange={(e) => setCashGiven(e.target.value)}
                  className="h-14 rounded-none border-slate-700 bg-[#0b1118] text-center text-2xl font-black tabular-nums text-white placeholder:text-muted-foreground"
                  autoFocus
                  disabled={interactionLocked}
                />
              </div>

              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Rueckgeld</p>
                <div className={`flex h-14 items-center justify-between border px-3 ${
                  cashNum >= totalWithTip ? 'border-emerald-500 bg-emerald-950/30' : 'border-slate-700 bg-[#0b1118]'
                }`}>
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Ausgabe</span>
                  <span className={`text-2xl font-black tabular-nums ${
                    cashNum >= totalWithTip ? 'text-emerald-300' : 'text-muted-foreground'
                  }`}>
                    €{change.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <Button
              onClick={confirmCash}
              disabled={cashNum < totalWithTip || interactionLocked}
              className="h-12 w-full rounded-none bg-emerald-600 text-base font-black hover:bg-emerald-700"
            >
              {isProcessing ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Barzahlung abschliessen'}
            </Button>
          </div>
        </div>
      );
    }

    if (isIntegratedCardMethodId(method) && selectedIntegratedMethod) {
      return (
        <div className="border border-slate-700 bg-[#16202c] p-3">
          <div className="mb-3 flex items-center justify-between gap-3 border-b border-slate-700 pb-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Terminal</p>
              <p className="mt-1 text-sm font-bold text-white">{selectedIntegratedMethod.name}</p>
            </div>
            {cardStatus === 'error' ? (
              <Button
                variant="outline"
                className="h-10 rounded-none border-slate-700 bg-[#101722] px-3 text-slate-100 hover:bg-[#1b2633]"
                onClick={() => startIntegratedCardPayment(selectedIntegratedMethod.id)}
                disabled={interactionLocked}
              >
                <RotateCcw className="mr-2 h-4 w-4" /> Nochmal
              </Button>
            ) : null}
          </div>
          <CardTerminalStatus
            status={cardStatus}
            total={totalWithTip}
            errorMessage={cardErrorMessage}
            onRetry={() => startIntegratedCardPayment(selectedIntegratedMethod.id)}
            onCancel={clearSelectedMethod}
          />
        </div>
      );
    }

    if (method === 'card_manual') {
      return (
        <div className="border border-slate-700 bg-[#16202c]">
          <div className="border-b border-slate-700 bg-[#101722] px-3 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Kartenzahlung manuell</p>
            <p className="mt-1 text-sm text-slate-300">Gast zahlt am externen Terminal. Danach hier sofort abschliessen.</p>
          </div>
          <div className="flex items-center justify-between px-3 py-4">
            <span className="text-sm font-semibold text-slate-300">Zu verbuchen</span>
            <span className="text-3xl font-black tabular-nums text-white">€{totalWithTip.toFixed(2)}</span>
          </div>
          <div className="border-t border-slate-700 p-3">
            <Button
              onClick={confirmManualCard}
              disabled={interactionLocked}
              className="h-12 w-full rounded-none bg-slate-800 text-base font-black hover:bg-black"
            >
              {isProcessing ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Kartenzahlung bestaetigen'}
            </Button>
          </div>
        </div>
      );
    }

    if (method === 'bank_transfer') {
      return (
        <div className="border border-slate-700 bg-[#16202c]">
          <div className="border-b border-slate-700 bg-[#101722] px-3 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Ueberweisung</p>
            <p className="mt-1 text-sm text-slate-300">Fuer externe oder spaetere Zahlungseingaenge ohne Terminal.</p>
          </div>
          <div className="flex items-center justify-between px-3 py-4">
            <span className="text-sm font-semibold text-slate-300">Zu verbuchen</span>
            <span className="text-3xl font-black tabular-nums text-white">€{totalWithTip.toFixed(2)}</span>
          </div>
          <div className="border-t border-slate-700 p-3">
            <Button
              onClick={confirmBankTransfer}
              disabled={interactionLocked}
              className="h-12 w-full rounded-none bg-amber-500 text-base font-black hover:bg-amber-600"
            >
              {isProcessing ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Ueberweisung verbuchen'}
            </Button>
          </div>
        </div>
      );
    }

    if (method === 'customer_card') {
      return (
        <div className="border border-slate-700 bg-[#16202c]">
          <div className="border-b border-slate-700 bg-[#101722] px-3 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Kundenkarte</p>
            <p className="mt-1 text-sm text-slate-300">Kundenkarte oder Kundenkonto belasten und danach sofort verbuchen.</p>
          </div>
          <div className="flex items-center justify-between px-3 py-4">
            <span className="text-sm font-semibold text-slate-300">Zu verbuchen</span>
            <span className="text-3xl font-black tabular-nums text-white">€{totalWithTip.toFixed(2)}</span>
          </div>
          <div className="border-t border-slate-700 p-3">
            <Button
              onClick={confirmCustomerCard}
              disabled={interactionLocked}
              className="h-12 w-full rounded-none bg-cyan-700 text-base font-black hover:bg-cyan-800"
            >
              {isProcessing ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Kundenkarte verbuchen'}
            </Button>
          </div>
        </div>
      );
    }

    if (method === 'internal') {
      return (
        <div className="border border-slate-700 bg-[#16202c]">
          <div className="border-b border-slate-700 bg-[#101722] px-3 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Eigenverbrauch</p>
            <p className="mt-1 text-sm text-slate-300">Interne Buchung ohne Zahlungseingang. Trinkgeld wird nicht uebernommen.</p>
          </div>
          <div className="flex items-center justify-between px-3 py-4">
            <span className="text-sm font-semibold text-slate-300">Zu verbuchen</span>
            <span className="text-3xl font-black tabular-nums text-white">€{baseTotal.toFixed(2)}</span>
          </div>
          <div className="border-t border-slate-700 p-3">
            <Button
              onClick={confirmInternalBooking}
              disabled={interactionLocked}
              className="h-12 w-full rounded-none bg-slate-900 text-base font-black hover:bg-black"
            >
              {isProcessing ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Eigenverbrauch abschliessen'}
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    if (!open) return;

    setCashGiven('');
    setCardStatus('idle');
    setCardErrorMessage('');
    const nextSplitMode = initialSplitMode === 'split' && allowSplitPayments ? 'split' : 'shared';
    setSplitMode(nextSplitMode);
    setSplitStep(nextSplitMode === 'split' ? 'selection' : 'payment');
    setSelectedSplitUnits(nextSplitMode === 'split'
      ? {}
      : {});

    if (defaultMethod && enabledMethods.some((entry) => entry.id === defaultMethod)) {
      setMethod(defaultMethod);
      if (
        nextSplitMode !== 'split'
        && isIntegratedCardMethodId(defaultMethod)
        && getIntegratedMethodAvailability(
        enabledMethods.find((entry) => entry.id === defaultMethod) || {},
      ).available
      ) {
        setTimeout(() => startIntegratedCardPayment(defaultMethod), 100);
      }
      return;
    }

    setMethod(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultMethod, enabledMethods, gpTomConfigured, initialSplitMode, isOnline, allowSplitPayments, sumUpConfigured]);

  useEffect(() => {
    if (!paymentSelectionMissing || !method) return;
    clearSelectedMethod();
  }, [method, paymentSelectionMissing]);

  const paymentMethodCards = enabledMethods.map((entry) => {
    const integrated = isIntegratedCardMethodId(entry.id);
    const availability = integrated ? getIntegratedMethodAvailability(entry) : { available: true, message: '' };
    const disabled = isChoosingSplit || paymentSelectionMissing || interactionLocked || !availability.available;
    const selected = method === entry.id;

    const presentation = {
      cash: { label: 'Barzahlung', hint: 'Wechselgeld', icon: Banknote, activeClass: 'bg-emerald-600', activeHintClass: 'text-emerald-100' },
      card: { label: 'GP TOM Karte', hint: availability.available ? 'Terminal direkt' : 'Nicht bereit', icon: CreditCard, activeClass: 'bg-blue-700', activeHintClass: 'text-blue-100' },
      card_sumup: { label: 'SumUp Karte', hint: availability.available ? 'Terminal direkt' : 'Nicht bereit', icon: CreditCard, activeClass: 'bg-blue-700', activeHintClass: 'text-blue-100' },
      card_manual: { label: 'Bankomat', hint: 'Externes Geraet', icon: CreditCard, activeClass: 'bg-slate-800', activeHintClass: 'text-slate-300' },
      customer_card: { label: 'Kundenkarte', hint: 'Kundenkonto', icon: CreditCard, activeClass: 'bg-cyan-700', activeHintClass: 'text-cyan-100' },
      bank_transfer: { label: 'Ueberweisung', hint: 'Extern buchen', icon: Banknote, activeClass: 'bg-amber-500', activeHintClass: 'text-amber-100' },
      internal: { label: 'Eigenverbrauch', hint: 'Ohne Zahlung', icon: ClipboardList, activeClass: 'bg-black', activeHintClass: 'text-slate-300' },
    }[entry.id] || {
      label: entry.name,
      hint: entry.description || 'Zahlart',
      icon: CreditCard,
      activeClass: 'bg-slate-800',
      activeHintClass: 'text-slate-300',
    };

    return { ...entry, ...presentation, disabled, selected };
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="pos-workflow-dialog pos-payment-dialog left-1/2 top-auto bottom-0 grid h-[94vh] max-h-[94vh] w-screen max-w-none translate-x-[-50%] translate-y-0 gap-0 overflow-hidden rounded-none border p-0 shadow-2xl [&>button]:hidden sm:top-[50%] sm:bottom-auto sm:h-[min(90vh,48rem)] sm:w-[min(96vw,68rem)] sm:max-h-[90vh] sm:translate-y-[-50%]">
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="border-b border-slate-800 bg-[#101722] px-4 py-3 sm:px-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <DialogTitle className="text-xl font-black text-white sm:text-2xl">Zahlung</DialogTitle>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                  {splitMode === 'split'
                    ? `${splitSelection.selectedCount} Artikel fuer Teilzahlung markiert`
                    : `${totalUnitCount} Artikel in gemeinsamer Rechnung`}
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="border border-slate-700 bg-[#020617] px-3 py-2 text-right text-white">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-300">Zu zahlen</p>
                  <p className="text-2xl font-black tabular-nums sm:text-3xl">€{totalWithTip.toFixed(2)}</p>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex h-11 w-11 items-center justify-center border border-slate-700 bg-[#111827] text-slate-300 transition-colors hover:bg-[#1f2937] hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1fr)_24rem] lg:grid-rows-none">
            <div className="order-1 min-h-0 overflow-hidden border-b border-slate-800 lg:border-b-0 lg:border-r">
              <div className="flex h-full min-h-0 flex-col gap-2 p-2.5 sm:gap-3 sm:p-4">
                <div className="border border-slate-700 bg-[#16202c]">
                  <div className="grid grid-cols-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSplitMode('shared');
                        setSplitStep('payment');
                        setSelectedSplitUnits({});
                      }}
                      disabled={interactionLocked}
                      className={`px-3 py-2.5 text-left transition-colors ${
                        splitMode === 'shared'
                          ? 'bg-slate-900 text-white'
                          : 'bg-[#16202c] text-slate-200 hover:bg-[#1b2633]'
                      } ${interactionLocked ? 'cursor-not-allowed opacity-60' : ''}`}
                    >
                        <span className="block text-sm font-bold">Gemeinsam</span>
                        <span className={`mt-1 block text-[11px] uppercase tracking-[0.18em] ${
                          splitMode === 'shared' ? 'text-slate-300' : 'text-muted-foreground'
                        }`}>
                          Ganze Rechnung
                        </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!allowSplitPayments) return;
                        setSplitMode('split');
                        setSplitStep('selection');
                        setSelectedSplitUnits({});
                      }}
                      disabled={!allowSplitPayments || interactionLocked}
                      className={`border-l border-slate-700 px-3 py-2.5 text-left transition-colors ${
                        splitMode === 'split'
                          ? 'bg-blue-700 text-white'
                          : 'bg-[#16202c] text-slate-200 hover:bg-[#1b2633]'
                      } ${(!allowSplitPayments || interactionLocked) ? 'cursor-not-allowed opacity-50' : ''}`}
                    >
                      <span className="block text-sm font-bold">Getrennt</span>
                      <span className={`mt-1 block text-[11px] uppercase tracking-[0.18em] ${
                        splitMode === 'split' ? 'text-blue-100' : 'text-muted-foreground'
                      }`}>
                        Artikel markieren
                      </span>
                    </button>
                  </div>
                  {!allowSplitPayments ? (
                    <div className="border-t border-slate-700 bg-[#101722] px-4 py-2 text-xs text-muted-foreground">
                      Getrennt zahlen ist verfuegbar, sobald ein offener Tischvorgang geladen ist.
                    </div>
                  ) : null}
                </div>

                {splitMode === 'split' ? (
                  <div className="flex min-h-0 flex-1 flex-col border border-slate-700 bg-[#16202c]">
                    <div className="flex items-center justify-between border-b border-slate-700 bg-[#101722] px-4 py-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Artikel splitten</p>
                        <p className="mt-1 hidden text-sm text-slate-300 sm:block">
                          {isChoosingSplit ? 'Tippe die Artikel an, die jetzt bezahlt werden.' : 'Auswahl ist bereit. Fuer eine andere Auswahl erneut markieren.'}
                        </p>
                      </div>
                      <div className="flex gap-2 text-xs font-semibold">
                        <button
                          type="button"
                          onClick={selectAllSplitUnits}
                          disabled={interactionLocked}
                          className="text-blue-300 transition-colors hover:text-blue-200 disabled:opacity-40"
                        >
                          Alle
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedSplitUnits({})}
                          disabled={interactionLocked}
                          className="text-muted-foreground transition-colors hover:text-slate-200 disabled:opacity-40"
                        >
                          Keine
                        </button>
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                      {splitUnits.map((unit) => {
                        const isSelected = Boolean(selectedSplitUnits[unit.unitId]);
                        return (
                          <button
                            key={unit.unitId}
                            type="button"
                            onClick={() => setSelectedSplitUnits((current) => ({
                              ...current,
                              [unit.unitId]: !current[unit.unitId],
                            }))}
                            disabled={interactionLocked}
                            className={`grid w-full grid-cols-[1.5rem_minmax(0,1fr)_auto] items-center gap-3 border-b border-slate-700 px-3 py-2.5 text-left transition-colors last:border-b-0 sm:px-4 sm:py-3 ${
                              isSelected ? 'bg-blue-950/40 text-white' : 'bg-[#16202c] text-slate-300 hover:bg-[#1b2633]'
                            } ${interactionLocked ? 'cursor-not-allowed opacity-60' : ''}`}
                          >
                            <div className={`h-5 w-5 border ${
                              isSelected ? 'border-blue-700 bg-blue-700' : 'border-slate-500 bg-[#0b1118]'
                            }`}>
                              {isSelected ? <CheckCircle2 className="h-5 w-5 text-white" /> : null}
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-[13px] font-semibold sm:text-sm">{unit.product_name}</p>
                              {unit.modifiers?.length ? (
                                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                  {unit.modifiers.map((modifier) => modifier.name).join(', ')}
                                </p>
                              ) : null}
                            </div>
                            <span className="text-[13px] font-black tabular-nums text-white sm:text-sm">€{unit.price.toFixed(2)}</span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="grid grid-cols-2 border-t border-slate-700 bg-[#020617] text-white">
                      <div className="px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300">Markiert</p>
                        <p className="mt-1 text-2xl font-black">{splitSelection.selectedCount}</p>
                      </div>
                      <div className="border-l border-slate-700 px-4 py-3 text-right">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300">Zwischensumme</p>
                        <p className="mt-1 text-2xl font-black tabular-nums">€{splitSelection.total.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="border-t border-slate-700 bg-[#101722] p-3">
                      <Button
                        type="button"
                        onClick={() => {
                          if (isChoosingSplit) {
                            if (paymentSelectionMissing) {
                              toast.warning('Bitte mindestens einen Artikel markieren.');
                              return;
                            }
                            setSplitStep('payment');
                            return;
                          }
                          setSplitStep('selection');
                        }}
                        disabled={interactionLocked}
                        className="h-11 w-full rounded-none bg-emerald-600 text-base font-black hover:bg-emerald-500"
                      >
                        {isChoosingSplit ? 'Weiter zur Zahlart' : 'Auswahl aendern'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="border border-slate-700 bg-[#16202c] lg:hidden">
                      <div className="flex items-center justify-between px-4 py-3">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Gemeinsame Rechnung</p>
                          <p className="mt-1 hidden text-sm text-slate-300 sm:block">Alles in einem Schritt.</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Zwischensumme</p>
                          <p className="mt-1 text-2xl font-black tabular-nums text-white">€{finalTotal.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                    <div className="hidden border border-slate-700 bg-[#16202c] lg:block">
                      <div className="border-b border-slate-700 bg-[#101722] px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Gesamtrechnung</p>
                        <p className="mt-1 text-sm text-slate-300">Die komplette offene Bestellung wird in einem Schritt abgeschlossen.</p>
                      </div>
                      <div className="grid grid-cols-2">
                        <div className="px-4 py-4">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Artikel</p>
                          <p className="mt-1 text-3xl font-black text-white">{totalUnitCount}</p>
                        </div>
                        <div className="border-l border-slate-700 px-4 py-4 text-right">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Zwischensumme</p>
                          <p className="mt-1 text-3xl font-black tabular-nums text-white">€{finalTotal.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="order-2 shrink-0 border-t border-slate-800 lg:min-h-0 lg:overflow-y-auto lg:border-t-0">
              <div className="space-y-2 p-2.5 sm:space-y-3 sm:p-4">
                <div className="border border-slate-700 bg-[#16202c]">
                  <div className="border-b border-slate-700 bg-[#101722] px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Beleg</p>
                    <p className="mt-1 hidden text-sm text-slate-300 lg:block">Gleiche Einstellung fuer jede Zahlart und jedes Geraet.</p>
                  </div>
                  <div className="border-t border-slate-700 bg-[#16202c]">
                    <button
                      type="button"
                      onClick={() => onReceiptEnabledChange(!receiptEnabled)}
                      disabled={interactionLocked}
                      className={`flex min-h-[48px] w-full flex-col justify-between px-3 py-2 text-left transition-colors ${
                        receiptEnabled
                          ? 'bg-emerald-600 text-white'
                          : 'bg-[#16202c] text-white hover:bg-[#1b2633]'
                      } ${interactionLocked ? 'cursor-not-allowed opacity-50' : ''}`}
                    >
                      <span className="text-sm font-bold">Mit Beleg</span>
                      <span className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${
                        receiptEnabled ? 'text-emerald-100' : 'text-muted-foreground'
                      }`}>
                        {receiptEnabled ? 'Automatisch' : 'Aus'}
                      </span>
                    </button>
                  </div>
                </div>

                <div className="border border-slate-700 bg-[#16202c]">
                  <div className="border-b border-slate-700 bg-[#101722] px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Zahlarten</p>
                    <p className="mt-1 hidden text-sm text-slate-300 lg:block">Gleiche Zahlarten und Reihenfolge auf Handy, Tablet und Desktop.</p>
                  </div>

                  <div className="grid grid-cols-2 gap-px border-t border-slate-700 bg-slate-800">
                    {paymentMethodCards.map((paymentMethod) => {
                      const Icon = paymentMethod.icon;
                      return (
                        <button
                          key={paymentMethod.id}
                          type="button"
                          onClick={() => selectMethod(paymentMethod.id)}
                          disabled={paymentMethod.disabled}
                          className={`flex min-h-[60px] flex-col justify-between px-3 py-2.5 text-left transition-colors ${
                            paymentMethod.selected
                              ? `${paymentMethod.activeClass} text-white`
                              : 'bg-[#16202c] text-white hover:bg-[#1b2633]'
                          } ${paymentMethod.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                        >
                          <span className="flex items-center gap-2 text-sm font-bold">
                            <Icon className="h-4 w-4" /> {paymentMethod.label}
                          </span>
                          <span className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${
                            paymentMethod.selected ? paymentMethod.activeHintClass : 'text-muted-foreground'
                          }`}>
                            {paymentMethod.hint}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="lg:hidden">
                  {(method || isChoosingSplit || paymentSelectionMissing) ? renderActionPanel() : null}
                </div>
                <div className="hidden lg:block">
                  {renderActionPanel()}
                </div>

                <div className="hidden border border-slate-700 bg-[#16202c] lg:block">
                  <div className="grid grid-cols-3">
                    <div className="px-3 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Artikel</p>
                      <p className="mt-1 text-2xl font-black text-white">{selectedItemCount}</p>
                    </div>
                    <div className="border-l border-slate-700 px-3 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Tip</p>
                      <p className="mt-1 text-2xl font-black tabular-nums text-white">€{appliedTip.toFixed(2)}</p>
                    </div>
                    <div className="border-l border-slate-700 bg-[#020617] px-3 py-3 text-white">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300">Total</p>
                      <p className="mt-1 text-2xl font-black tabular-nums">€{totalWithTip.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                <div className="hidden border border-slate-700 bg-[#16202c] lg:block">
                  <div className="border-b border-slate-700 bg-[#101722] px-3 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Trinkgeld</p>
                    <p className="mt-1 hidden text-sm text-slate-300 sm:block">
                      {tipLocked ? 'Bei Eigenverbrauch ist Trinkgeld deaktiviert.' : 'Tip vor der Zahlart festlegen.'}
                    </p>
                  </div>
                  <div className="grid grid-cols-5 gap-px border-t border-slate-700 bg-slate-800">
                    {QUICK_TIPS.map((quickTip) => (
                      <button
                        key={quickTip}
                        type="button"
                        onClick={() => setTip(quickTip)}
                        disabled={tipLocked || isChoosingSplit || paymentSelectionMissing || interactionLocked}
                        className={`h-11 text-sm font-semibold transition-colors ${
                          tip === quickTip && !tipLocked
                            ? 'bg-slate-900 text-white'
                            : 'bg-[#16202c] text-white hover:bg-[#1b2633]'
                        } ${(tipLocked || isChoosingSplit || paymentSelectionMissing || interactionLocked) ? 'cursor-not-allowed opacity-40' : ''}`}
                      >
                        {quickTip === 0 ? 'Kein' : `€${quickTip.toFixed(2)}`}
                      </button>
                    ))}
                  </div>
                </div>

                {integratedMethodWarnings.length > 0 ? (
                  <div className="border border-amber-300/40 bg-amber-950/30 px-3 py-3 text-xs text-amber-100">
                    {integratedMethodWarnings.map((warning) => (
                      <p key={warning}>{warning}</p>
                    ))}
                  </div>
                ) : null}

              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 bg-[#101722] px-4 py-3 sm:px-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Button variant="outline" className="h-11 rounded-none border-slate-700 bg-[#111827] px-4 text-slate-100 hover:bg-[#1f2937]" onClick={handleClose}>
                Abbrechen
              </Button>
              <p className="hidden text-xs text-muted-foreground sm:block sm:text-right">{receiptHint}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Table quick-pick popup (gastronovi-style: numpad + list, pick either way) ──
const DIALPAD_ROWS = [['1', '2', '3'], ['4', '5', '6'], ['7', '8', '9']];

function tableStatusColor(table) {
  if (table.status === 'occupied') return '#fb7185';
  if (table.status === 'reserved') return '#fbbf24';
  return '#e2e8f0';
}

function findTableByNumber(tables, query) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return null;
  return tables.find((t) => {
    const name = String(t.name || '').toLowerCase();
    const digits = name.replace(/\D+/g, '');
    return name === normalized || digits === normalized || name.includes(normalized);
  }) || null;
}

function TableQuickPickDialog({ open, onClose, tables, onSelectTable, onNoTable }) {
  const [dialDigits, setDialDigits] = useState('');

  useEffect(() => {
    if (open) setDialDigits('');
  }, [open]);

  const visibleTables = dialDigits
    ? tables.filter((t) => String(t.name || '').toLowerCase().replace(/\D+/g, '').includes(dialDigits))
    : tables;

  const handleOk = () => {
    const match = findTableByNumber(tables, dialDigits);
    if (!match) {
      toast.warning(`Kein Tisch "${dialDigits}" gefunden`);
      return;
    }
    onSelectTable(match);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="pos-workflow-dialog max-w-sm mx-4 gap-0 overflow-hidden rounded-none border p-0 [&>button]:hidden">
        <div className="pos-dialog-surface flex items-center justify-between border-b border-border px-3 py-2.5">
          <button onClick={onClose} className="text-sm font-semibold text-muted-foreground hover:text-foreground">
            Abbrechen
          </button>
          <DialogTitle className="text-sm font-bold">Tisch wählen</DialogTitle>
          <button
            onClick={onNoTable}
            className="pos-dialog-primary px-3 py-1.5 text-xs"
          >
            Kein Tisch
          </button>
        </div>

        <div className="grid grid-cols-3 border-b border-border">
          <div className="col-span-2 flex items-center justify-center border-r border-border bg-muted px-3 py-3 text-xl font-bold tabular-nums text-foreground">
            {dialDigits || <span className="text-sm font-normal text-muted-foreground">Tisch-Nr.</span>}
          </div>
          <button
            onClick={() => setDialDigits('')}
            className="flex items-center justify-center bg-foreground py-3 text-sm font-bold text-background hover:opacity-90"
          >
            C
          </button>
        </div>

        <div className="grid grid-cols-3 bg-card">
          {DIALPAD_ROWS.flat().map((digit) => (
            <button
              key={digit}
              onClick={() => setDialDigits((d) => (d.length < 4 ? d + digit : d))}
              className="border-b border-r border-border bg-card py-3 text-lg font-semibold text-foreground hover:bg-muted [&:nth-child(3n)]:border-r-0"
            >
              {digit}
            </button>
          ))}
          <button
            onClick={() => setDialDigits((d) => d.slice(0, -1))}
            className="flex items-center justify-center border-b border-r border-border bg-card py-3 text-foreground hover:bg-muted"
          >
            <Delete className="h-4 w-4" />
          </button>
          <button
            onClick={() => setDialDigits((d) => (d.length < 4 ? d + '0' : d))}
            className="border-b border-r border-border bg-card py-3 text-lg font-semibold text-foreground hover:bg-muted"
          >
            0
          </button>
          <button
            onClick={handleOk}
            className="pos-dialog-primary border-b border-border py-3 text-sm"
          >
            OK
          </button>
        </div>

        <div className="max-h-64 overflow-y-auto">
          {visibleTables.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">Keine Tische gefunden</p>
          ) : (
            visibleTables.map((table) => (
              <button
                key={table.id}
                onClick={() => onSelectTable(table)}
                className="flex w-full items-center gap-2 border-b border-border bg-card px-3 py-2.5 text-left hover:bg-muted"
              >
                <span className="h-8 w-1.5 shrink-0 rounded-sm" style={{ backgroundColor: tableStatusColor(table) }} />
                <span className="flex-1 text-sm font-semibold text-foreground">{table.name}</span>
                {table.capacity ? (
                  <span className="text-xs text-muted-foreground">{table.capacity} Plätze</span>
                ) : null}
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function isAppleMobileDevice() {
  if (typeof window === 'undefined') return false;

  const ua = window.navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) || (
    window.navigator.platform === 'MacIntel' &&
    window.navigator.maxTouchPoints > 1
  );
}

function isStandaloneDisplayMode() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function blurActiveElement() {
  if (typeof document === 'undefined') return;
  const active = document.activeElement;
  if (active && typeof active.blur === 'function') {
    active.blur();
  }
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Swipe-left-to-reveal for a ticket line's Schließen/Stornieren/Rabatt actions
// (Gastronovi Kassensystem convention). A real top-level component — not a
// helper called from inside a .map() — so useSwipeable/useState get one stable
// hook instance per rendered line regardless of how many cart lines exist.
function SwipeReveal({ children, actions, disabled = false }) {
  const [revealed, setRevealed] = useState(false);
  const handlers = useSwipeable({
    onSwipedLeft: () => setRevealed(true),
    onSwipedRight: () => setRevealed(false),
    trackMouse: true,
    preventScrollOnSwipe: true,
    delta: 40,
  });

  if (disabled) return children;

  return (
    <div className="relative overflow-hidden">
      <div
        {...handlers}
        className="transition-transform duration-200 ease-out"
        style={{ transform: revealed ? 'translateX(-100%)' : 'translateX(0)' }}
      >
        {children}
      </div>
      <div
        className="absolute inset-0 flex items-stretch gap-px transition-transform duration-200 ease-out"
        style={{ transform: revealed ? 'translateX(0)' : 'translateX(100%)' }}
      >
        {actions({ close: () => setRevealed(false) })}
      </div>
    </div>
  );
}

function getApiErrorMessage(error, fallback) {
  return error?.response?.data?.detail || error?.message || fallback;
}

// ─── Main POS Terminal ─────────────────────────────────────────────────────────
export default function PosTerminal() {
  const [categories, setCategories]         = useState([]);
  const [allProducts, setAllProducts]       = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading]           = useState(true);
  const [isProcessing, setIsProcessing]     = useState(false);
  const [showReceipt, setShowReceipt]       = useState(false);
  const [showDiscount, setShowDiscount]     = useState(false);
  const [stornoDialog, setStornoDialog]     = useState(null); // { item } | { item: null } for whole-order
  const [rabattDialog, setRabattDialog]     = useState(null); // { item }
  const [showDiverseDialog, setShowDiverseDialog] = useState(false);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [showPayDialog, setShowPayDialog]   = useState(false);
  const [checkoutStartMode, setCheckoutStartMode] = useState('shared'); // shared | split
  const [defaultPayMethod, setDefaultPayMethod] = useState(null); // payment method id | null
  const [completedOrder, setCompletedOrder] = useState(null);
  const [autoOpenReceipt, setAutoOpenReceipt] = useState(true);
  const [optionGroups, setOptionGroups]     = useState([]);
  const [modifierProduct, setModifierProduct] = useState(null); // product awaiting modifier selection
  const [selectedModifiers, setSelectedModifiers] = useState({}); // groupId → optionId(s)
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountAmount, setDiscountAmount]   = useState(0);
  const [discountReason, setDiscountReason]   = useState('');
  const [tipAmount, setTipAmount]             = useState(0);
  const [fulfillmentType, setFulfillmentType] = useState('dine-in'); // dine-in | takeaway | delivery
  const [serviceCourse, setServiceCourse]     = useState('Sofort'); // Sofort | Gang 1 | Gang 2 | Gang 3
  const [productSearch, setProductSearch]     = useState('');
  const [orderPanelWidth, setOrderPanelWidth] = useState('normal'); // normal | wide | xwide
  const [categoryLayout, setCategoryLayout]   = useState({ order: [], hiddenIds: [] });
  const [salesCatalog, setSalesCatalog] = useState(() => buildEmptySalesCatalog());
  const [posTheme, setPosTheme] = useState(() => ({ ...POS_DEFAULT_TERMINAL_THEME }));
  const [categoryAppearance, setCategoryAppearance] = useState({});
  const [articleCourses, setArticleCourses] = useState({});
  const [featuredCategoryIds, setFeaturedCategoryIds] = useState([]);
  const [featuredProductIds, setFeaturedProductIds] = useState([]);
  const [activeSalesMenuId, setActiveSalesMenuId] = useState('');
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [canInstallPwa, setCanInstallPwa] = useState(false);
  const [showIosInstallHint, setShowIosInstallHint] = useState(false);
  const [isStandaloneApp, setIsStandaloneApp] = useState(() => isStandaloneDisplayMode());
  const [posSettings, setPosSettings] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const handledPaymentIntentRef = useRef('');
  const { user } = useAuth();
  const userPermissions = new Set([
    ...(Array.isArray(user?.effective_permissions) ? user.effective_permissions : []),
    ...(Array.isArray(user?.permissions) ? user.permissions : []),
  ]);
  const hasPerm = (perm) => {
    if (['admin', 'manager', 'superadmin'].includes(String(user?.role || '').toLowerCase())) return true;
    return userPermissions.has('all') || userPermissions.has(perm);
  };
  const { isOnline, offlineData, savePendingOrder } = useOffline();
  const { items, addItem, removeItem, updateQuantity, clearCart, subtotal, total, itemCount, selectedTable, setSelectedTable, activeOrderId, setActiveOrderId, loadOrder } = useCart();
  const [allTables, setAllTables] = useState([]);
  const [showTablePicker, setShowTablePicker] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (!isOnline) return;
    getPosLayoutConfig()
      .then((config) => {
        setOrderPanelWidth(config?.orderPanelWidth || 'normal');
        setCategoryLayout({
          order: Array.isArray(config?.categoryLayout?.order) ? config.categoryLayout.order : [],
          hiddenIds: Array.isArray(config?.categoryLayout?.hiddenIds) ? config.categoryLayout.hiddenIds : [],
        });
        setPosTheme({
          ...POS_DEFAULT_TERMINAL_THEME,
          ...(config?.theme || {}),
        });
        setCategoryAppearance(
          config?.categoryAppearance && typeof config.categoryAppearance === 'object'
            ? config.categoryAppearance
            : {},
        );
        setArticleCourses(
          config?.articleCourses && typeof config.articleCourses === 'object'
            ? config.articleCourses
            : {},
        );
        setFeaturedCategoryIds(Array.isArray(config?.featuredCategoryIds) ? config.featuredCategoryIds : []);
        setFeaturedProductIds(Array.isArray(config?.featuredProductIds) ? config.featuredProductIds : []);
        setSalesCatalog(normalizeSalesCatalog(config?.salesCatalog || buildEmptySalesCatalog()));
      })
      .catch(() => {});
  }, [isOnline]);

  useEffect(() => {
    const fulfillmentFromState = location.state?.fulfillmentType;
    if (!fulfillmentFromState) return;

    setFulfillmentType(fulfillmentFromState === 'pickup' ? 'takeaway' : fulfillmentFromState);
  }, [location.state?.fulfillmentType]);

  useEffect(() => {
    const paymentIntentId = location.state?.paymentIntentId;
    if (!location.state?.openPayment || !paymentIntentId) return;
    if (handledPaymentIntentRef.current === paymentIntentId) return;
    const paymentOrder = location.state?.paymentOrder;
    if (paymentOrder?.id && activeOrderId !== paymentOrder.id) {
      loadOrder(paymentOrder);
      return;
    }
    if (items.length === 0) return;

    handledPaymentIntentRef.current = paymentIntentId;
    setDefaultPayMethod(location.state?.defaultMethod || null);
    setCheckoutStartMode('shared');
    setShowPayDialog(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [activeOrderId, items.length, loadOrder, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(display-mode: standalone)');

    const syncInstallState = () => {
      const standalone = isStandaloneDisplayMode();
      setIsStandaloneApp(standalone);
      setShowIosInstallHint(isAppleMobileDevice() && !standalone);
    };

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPromptEvent(event);
      setCanInstallPwa(true);
    };

    const handleAppInstalled = () => {
      setInstallPromptEvent(null);
      setCanInstallPwa(false);
      syncInstallState();
    };

    syncInstallState();

    mediaQuery.addEventListener?.('change', syncInstallState);
    mediaQuery.addListener?.(syncInstallState);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      mediaQuery.removeEventListener?.('change', syncInstallState);
      mediaQuery.removeListener?.(syncInstallState);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      if (isOnline) {
        const [cats, prods, groups, tbls, settingsPayload] = await Promise.all([
          getCategories(),
          getProducts(),
          getArticleOptionGroups().catch(() => []),
          getTables().catch(() => []),
          getPosSettingsPayload().catch(() => null),
        ]);
        setCategories(cats);
        setAllProducts(prods);
        setOptionGroups(groups);
        setAllTables(tbls);
        setPosSettings(settingsPayload?.settings || null);
      } else {
        setCategories(offlineData.categories || []);
        setAllProducts(offlineData.products  || []);
        setPosSettings(null);
      }
    } catch {
      setCategories(offlineData.categories || []);
      setAllProducts(offlineData.products  || []);
      setPosSettings(null);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateDiscountedTotal = useCallback(() => {
    if (discountPercent > 0) return total * (1 - discountPercent / 100);
    return total - discountAmount;
  }, [total, discountPercent, discountAmount]);

  const finalTotal = Math.max(0, calculateDiscountedTotal());
  // A user who can access the POS must be able to close a sale. Role permissions
  // still control navigation and management screens, but must not hide every
  // configured payment method and leave an active order impossible to complete.
  const enabledPaymentMethods = getEnabledPaymentMethods(posSettings?.payment_methods);
  const canTakePayment = enabledPaymentMethods.length > 0;
  const gpTomConfigured = posSettings?.gp_tom_configured ?? isGpTomConfigured(posSettings);
  const sumUpConfigured = posSettings?.sumup_configured ?? isSumUpConfigured(posSettings);
  const hasProductSearch = productSearch.trim().length > 0;
  const posSalesScope = scopeFromFulfillment(fulfillmentType, 'pos');
  const scopedSalesMenus = getMenusForScope(salesCatalog, posSalesScope, 'pos_terminal');
  const activeSalesMenu = scopedSalesMenus.find((menu) => menu.id === activeSalesMenuId) || scopedSalesMenus[0] || null;
  const menuCatalogView = activeSalesMenu ? buildCatalogViewFromMenu(activeSalesMenu, allProducts) : null;
  const effectiveCategories = menuCatalogView?.categories || categories;
  const effectiveProducts = menuCatalogView?.products || allProducts;
  const selectedCategoryEntry = effectiveCategories.find((category) => category.id === selectedCategory) || null;
  const canReturnToCatalog = Boolean(selectedCategory || hasProductSearch);
  const backTargetLabel = activeSalesMenu?.name || 'Kategorien';

  useEffect(() => {
    if (!scopedSalesMenus.length) {
      setActiveSalesMenuId('');
      return;
    }
    if (!scopedSalesMenus.some((menu) => menu.id === activeSalesMenuId)) {
      setActiveSalesMenuId(scopedSalesMenus[0].id);
    }
  }, [activeSalesMenuId, scopedSalesMenus]);

  useEffect(() => {
    if (!selectedCategory) return;
    if (!effectiveCategories.some((category) => category.id === selectedCategory)) {
      setSelectedCategory(null);
    }
  }, [effectiveCategories, selectedCategory]);

  const handleBackToCatalog = useCallback(() => {
    setSelectedCategory(null);
    setProductSearch('');
  }, []);

  const filteredProducts = effectiveProducts.filter((product) => {
    if (selectedCategory && product.category_id !== selectedCategory) return false;

    const normalizedSearch = productSearch.trim().toLowerCase();
    if (!normalizedSearch) return true;

    const haystack = [
      product.name,
      product.description,
      product.barcode,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(normalizedSearch);
  });

  // Admin-configurable POS left menu: hide + reorder categories per manager settings
  // (Manager Portal → Terminals → "Kategorien-Reihenfolge"). Categories not explicitly
  // ordered yet keep their natural (backend) order, appended after the configured ones.
  const visibleCategories = (() => {
    if (menuCatalogView) return effectiveCategories;
    const hiddenIds = new Set(categoryLayout.hiddenIds || []);
    const visible = effectiveCategories.filter((cat) => !hiddenIds.has(cat.id));
    const orderIndex = new Map((categoryLayout.order || []).map((id, idx) => [id, idx]));
    return visible
      .map((cat, naturalIndex) => ({ cat, rank: orderIndex.has(cat.id) ? orderIndex.get(cat.id) : 1000 + naturalIndex }))
      .sort((a, b) => a.rank - b.rank)
      .map(({ cat }) => cat);
  })();
  const baseCategoriesById = useMemo(
    () => new Map(categories.map((category) => [category.id, category])),
    [categories],
  );
  const effectiveCategoriesById = useMemo(
    () => new Map(effectiveCategories.map((category) => [category.id, category])),
    [effectiveCategories],
  );
  const allProductsById = useMemo(
    () => new Map(allProducts.map((product) => [product.id, product])),
    [allProducts],
  );
  const effectiveProductsByMenuItem = useMemo(() => {
    const next = new Map();
    effectiveProducts.forEach((product) => {
      const menuItemId = String(product.__menu_item_id || product.menu_item_id || '').trim();
      if (!menuItemId) return;
      next.set(`${menuItemId}::${product.id}`, product);
    });
    return next;
  }, [effectiveProducts]);
  const categoryAppearanceByName = useMemo(() => {
    const nextAppearanceByName = {};
    categories.forEach((category) => {
      const normalizedName = normalizeCategoryAppearanceName(category?.name);
      if (!normalizedName) return;
      const configured = categoryAppearance?.[category.id];
      if (configured && typeof configured === 'object') {
        nextAppearanceByName[normalizedName] = configured;
      }
    });
    return nextAppearanceByName;
  }, [categories, categoryAppearance]);
  const getVisual = useCallback((category) => getCategoryVisual(category, {
    theme: posTheme,
    appearanceById: categoryAppearance,
    appearanceByName: categoryAppearanceByName,
  }), [categoryAppearance, categoryAppearanceByName, posTheme]);
  const resolveCategoryForProduct = useCallback((product) => {
    if (!product) return null;

    const candidateIds = [
      product.__menu_group_id,
      product.menu_group_id,
      product.category_id,
    ].filter(Boolean);

    for (const candidateId of candidateIds) {
      if (effectiveCategoriesById.has(candidateId)) return effectiveCategoriesById.get(candidateId);
      if (baseCategoriesById.has(candidateId)) return baseCategoriesById.get(candidateId);
    }

    return null;
  }, [baseCategoriesById, effectiveCategoriesById]);
  const getProductVisual = useCallback((product, categoryOverride) => {
    const category = typeof categoryOverride === 'undefined' ? resolveCategoryForProduct(product) : categoryOverride;
    return buildProductVisual(product, category, getVisual(category), articleCourses);
  }, [articleCourses, getVisual, resolveCategoryForProduct]);
  const getCartItemVisual = useCallback((item) => {
    const menuKey = item?.menu_item_id ? `${item.menu_item_id}::${item.product_id}` : '';
    const matchedProduct =
      (menuKey && effectiveProductsByMenuItem.get(menuKey)) ||
      allProductsById.get(item.product_id) ||
      null;
    const category =
      (matchedProduct && resolveCategoryForProduct(matchedProduct)) ||
      (item?.menu_group_id && effectiveCategoriesById.get(item.menu_group_id)) ||
      (matchedProduct?.category_id && baseCategoriesById.get(matchedProduct.category_id)) ||
      null;

    return buildProductVisual(
      matchedProduct || { id: item.product_id, name: item.product_name },
      category,
      getVisual(category),
      articleCourses,
    );
  }, [
    articleCourses,
    allProductsById,
    baseCategoriesById,
    effectiveCategoriesById,
    effectiveProductsByMenuItem,
    getVisual,
    resolveCategoryForProduct,
  ]);
  const featuredCategoryNames = useMemo(() => new Set(
    categories
      .filter((category) => featuredCategoryIds.includes(category.id))
      .map((category) => normalizeCategoryAppearanceName(category?.name))
      .filter(Boolean),
  ), [categories, featuredCategoryIds]);
  const featuredCategoriesOnHome = useMemo(() => visibleCategories.filter((category) => (
    featuredCategoryIds.includes(category.id) ||
    featuredCategoryNames.has(normalizeCategoryAppearanceName(category?.name))
  )), [featuredCategoryIds, featuredCategoryNames, visibleCategories]);
  const featuredProductsOnHome = useMemo(() => {
    const selected = [];
    const seen = new Set();
    effectiveProducts.forEach((product) => {
      if (!featuredProductIds.includes(product.id) || seen.has(product.id)) return;
      seen.add(product.id);
      selected.push(product);
    });
    return selected;
  }, [effectiveProducts, featuredProductIds]);
  const hasHomeFavorites = featuredCategoriesOnHome.length > 0 || featuredProductsOnHome.length > 0;
  const posBorderColor = posTheme.borderColor || GASTRONOVI_BORDER;
  const posHeaderColor = posTheme.headerColor || '#3f3d43';
  const posCanvasColor = posTheme.canvasColor || '#ffffff';
  const posRailColor = posTheme.railColor || '#f1f5f9';
  const posAccentColor = posTheme.accentColor || '#95c640';
  const posSummaryColor = posTheme.summaryColor || '#59585d';
  const selectedCategoryTone = selectedCategoryEntry ? getVisual(selectedCategoryEntry) : null;
  const fulfillmentLabels = {
    'dine-in': 'Dine-in',
    takeaway: 'Take away',
    delivery: 'Delivery',
  };
  const mobileSummaryLabel = [
    `${itemCount} Artikel`,
    fulfillmentLabels[fulfillmentType] || 'Dine-in',
    activeOrderId ? 'Sync aktiv' : 'Nicht gesendet',
  ].join(' · ');
  const showInstallShortcut = !isStandaloneApp && (canInstallPwa || showIosInstallHint);
  const catalogGridClass = 'grid grid-cols-2 gap-1.5 min-[420px]:grid-cols-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6';

  const handleInstallApp = useCallback(async () => {
    if (installPromptEvent?.prompt) {
      installPromptEvent.prompt();
      try {
        await installPromptEvent.userChoice;
      } catch {
        // Ignore prompt cancellations.
      }
      setInstallPromptEvent(null);
      setCanInstallPwa(false);
      return;
    }

    if (showIosInstallHint) {
      toast.info('Safari öffnen → Teilen → Zum Home-Bildschirm');
    }
  }, [installPromptEvent, showIosInstallHint]);

  const openOverlay = useCallback((openFn) => {
    blurActiveElement();
    openFn();
  }, []);

  const showPrintWarnings = useCallback((printJobs, fallbackMessage) => {
    const errors = Array.isArray(printJobs?.errors) ? printJobs.errors : [];
    // e.g. "Gerät 'Bar-PC' ist offline – der Druck startet, sobald es wieder online ist."
    const warnings = Array.isArray(printJobs?.warnings) ? printJobs.warnings : [];
    if (errors.length === 0 && printJobs?.printed !== false) {
      if (warnings[0]) toast.warning(warnings[0]);
      return;
    }
    const firstMessage = errors[0]?.message || printJobs?.message || fallbackMessage;
    if (firstMessage) {
      toast.warning(firstMessage);
    }
  }, []);

  const openPaymentDialog = useCallback((method = null, startMode = 'shared') => {
    setDefaultPayMethod(method);
    setCheckoutStartMode(startMode);
    setShowPayDialog(true);
  }, []);

  // Send items to kitchen/bar WITHOUT paying (creates or updates open order)
  const handleSendToKitchen = async () => {
    if (items.length === 0) { toast.error('Warenkorb ist leer'); return; }
    setIsProcessing(true);
    try {
      if (!isOnline) {
        // Offline: save as a pending order to be synced later
        const offlineOrder = {
          id: `offline-${Date.now()}`,
          items,
          table_id: selectedTable?.id ?? null,
          table_name: selectedTable?.name ?? null,
          discount_amount: discountAmount,
          discount_percent: discountPercent,
          cashier_id: user?.id || '',
          status: 'pending',
          is_synced: false,
          created_at: new Date().toISOString(),
        };
        await savePendingOrder(offlineOrder);
        toast.info('Offline gespeichert — wird synchronisiert wenn online');
        clearCart();
        return;
      }

      if ((discountAmount > 0 || discountPercent > 0) && discountReason.trim().length < 3) {
        toast.error('Bitte einen Rabatt-Grund eingeben');
        return;
      }

      const isExistingOrder = Boolean(activeOrderId);
      let orderId = activeOrderId;

      if (isExistingOrder) {
        const result = await updateOrderItems(orderId, items, discountAmount, discountPercent, discountReason);
        showPrintWarnings(result?.print_jobs, 'Produktionsbon konnte nicht vollständig gedruckt werden.');
      } else {
        const orderData = {
          items,
          table_id: selectedTable?.id ?? null,
          table_name: selectedTable?.name ?? null,
          discount_amount: discountAmount,
          discount_percent: discountPercent,
          discount_reason: discountReason,
          cashier_id: user?.id || '',
          fulfillment_type: fulfillmentType,
          order_source: 'pos',
        };
        const order = await createOrder(orderData);
        showPrintWarnings(order?.print_jobs, 'Produktionsbon konnte nicht vollständig gedruckt werden.');
        orderId = order.id;
        setActiveOrderId(orderId);
      }

      if (serviceCourse !== 'Sofort') {
        const result = await sendOrderCourse(orderId, serviceCourse);
        showPrintWarnings(result?.print_jobs, 'Produktionsbon konnte nicht vollständig gedruckt werden.');
        setServiceCourse('Sofort');
        toast.success(`${serviceCourse} an die Küche gesendet!`);
      } else {
        toast.success(isExistingOrder ? 'Bestellung aktualisiert!' : 'Bestellung gesendet!');
      }
      if (selectedTable) navigate('/pos/tables');
      else clearCart();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePickTable = async (table) => {
    setShowTablePicker(false);
    try {
      const orders = await getOrders('open', null, table.id);
      if (orders.length > 0) {
        toast.info('Dieser Tisch hat offene Parteien. Bitte wählen Sie den Vorgang in der Tischansicht.');
        navigate('/pos/tables');
        return;
      }
      setSelectedTable(table);
    } catch {
      setSelectedTable(table);
    }
  };

  const handleNoTable = () => {
    setShowTablePicker(false);
    setSelectedTable(null);
  };

  // Items already sent to the kitchen can't just be dropped from the cart — GoBD/RKSV
  // require an immutable record, so they must be voided (with a reason) instead.
  const handleRemoveOrVoidItem = (item) => {
    const key = item.cartKey || item.product_id;
    if (!item.isSent) {
      removeItem(key);
      return;
    }
    if (!activeOrderId) {
      toast.error('Keine aktive Bestellung gefunden');
      return;
    }
    setStornoDialog({ item });
  };

  const handleConfirmStorno = async ({ quantity, wasProduced, reasonCode }) => {
    const target = stornoDialog?.item;
    const reasonLabel = STORNO_REASON_LABELS[reasonCode] || reasonCode;
    if (target) {
      const key = target.cartKey || target.product_id;
      const updatedOrder = await voidOrderItem(activeOrderId, key, reasonLabel, { quantity, reasonCode, wasProduced });
      loadOrder(updatedOrder);
      toast.success('Artikel storniert');
    } else {
      await cancelOrder(activeOrderId, reasonLabel, { reasonCode, wasProduced });
      clearCart();
      toast.success('Bestellung storniert');
      navigate('/pos/tables');
    }
  };

  const handleConfirmRabatt = async ({ quantity, discountPercent, discountAmount, reason, account }) => {
    const target = rabattDialog?.item;
    if (!target || !activeOrderId) return;
    const key = target.cartKey || target.product_id;
    const updatedOrder = await discountOrderItem(activeOrderId, key, {
      quantity, discountPercent, discountAmount, reason, account,
    });
    loadOrder(updatedOrder);
    toast.success('Rabatt angewendet');
  };

  const handleConfirmDiverse = async ({ categoryId, description, price, vatRate, quantity }) => {
    let orderId = activeOrderId;
    if (!orderId) {
      const order = await createOrder({
        items: [],
        table_id: selectedTable?.id ?? null,
        table_name: selectedTable?.name ?? null,
        cashier_id: user?.id || '',
        fulfillment_type: fulfillmentType,
        order_source: 'pos',
      });
      orderId = order.id;
      setActiveOrderId(order.id);
    }
    const result = await addDiverseOrderItem(orderId, { categoryId, description, price, vatRate, quantity });
    showPrintWarnings(result?.print_jobs, 'Produktionsbon konnte nicht vollständig gedruckt werden.');
    loadOrder(result);
    toast.success(`"${description}" hinzugefügt`);
  };

  const buildManualCardPaymentDetails = () => ({
    payment_provider: 'manual_terminal',
    payment_reference: '',
    payment_metadata: {
      terminal_type: 'manual',
    },
  });

  const runGpTomCardPayment = async ({ amount, tip, reference, onStatusChange }) => {
    if (!isOnline) {
      throw new Error('Kartenzahlung via GP tom braucht eine aktive Internetverbindung.');
    }
    if (!gpTomConfigured) {
      throw new Error('GP tom ist noch nicht vollstaendig eingerichtet.');
    }

    onStatusChange?.('connecting');

    const started = await authorizeGpTomPayment({
      amount,
      tip_amount: tip,
      currency: posSettings?.currency || 'EUR',
      reference,
    });

    if (!started?.task_id) {
      throw new Error('GP tom hat keinen Zahlungstask zurueckgegeben.');
    }

    onStatusChange?.('waiting');

    const startedAt = Date.now();
    while ((Date.now() - startedAt) < TERMINAL_POLL_TIMEOUT_MS) {
      await wait(TERMINAL_POLL_INTERVAL_MS);
      onStatusChange?.('processing');

      const result = await getGpTomTaskStatus(started.task_id);
      if (result.normalized_status === 'pending') {
        onStatusChange?.(result.raw_status === 'CREATED' ? 'waiting' : 'processing');
        continue;
      }

      if (result.normalized_status === 'succeeded') {
        onStatusChange?.('success');
        return {
          payment_provider: 'gptom',
          payment_reference: result.transaction_id || result.task_id,
          payment_metadata: {
            task_id: result.task_id,
            raw_status: result.raw_status,
            entry_mode: result.entry_mode,
            cvm: result.cvm,
            brand: result.brand,
            masked_pan: result.masked_pan,
            masked_number_last4: result.masked_number_last4,
            auth_code: result.auth_code,
            receipt_number: result.receipt_number,
            batch_number: result.batch_number,
            sequence_number: result.sequence_number,
            reference_number: result.reference_number,
            aid: result.aid,
            terminal_tid: result.terminal_tid,
          },
        };
      }

      if (result.normalized_status === 'cancelled') {
        throw new Error(result.message || 'Kartenzahlung am Terminal wurde abgebrochen.');
      }

      throw new Error(result.message || 'GP tom Kartenzahlung fehlgeschlagen.');
    }

    throw new Error('GP tom hat nicht rechtzeitig geantwortet.');
  };

  const runSumUpCardPayment = async ({ amount, tip, reference, onStatusChange }) => {
    if (!isOnline) {
      throw new Error('Kartenzahlung via SumUp braucht eine aktive Internetverbindung.');
    }
    if (!sumUpConfigured) {
      throw new Error('SumUp ist noch nicht vollstaendig eingerichtet.');
    }

    onStatusChange?.('connecting');

    const started = await authorizeSumUpPayment({
      amount,
      tip_amount: tip,
      currency: posSettings?.currency || 'EUR',
      reference,
    });

    if (!started?.client_transaction_id) {
      throw new Error('SumUp hat keine Client-Transaktions-ID zurueckgegeben.');
    }

    onStatusChange?.('waiting');

    const startedAt = Date.now();
    while ((Date.now() - startedAt) < TERMINAL_POLL_TIMEOUT_MS) {
      await wait(TERMINAL_POLL_INTERVAL_MS);
      onStatusChange?.('processing');

      const result = await getSumUpTransactionStatus(started.client_transaction_id);
      if (result.normalized_status === 'pending') {
        onStatusChange?.(result.reader_state === 'WAITING_FOR_CARD' ? 'waiting' : 'processing');
        continue;
      }

      if (result.normalized_status === 'succeeded') {
        onStatusChange?.('success');
        return {
          payment_provider: 'sumup',
          payment_reference: result.transaction_id || result.transaction_code || result.client_transaction_id,
          payment_metadata: {
            client_transaction_id: result.client_transaction_id,
            transaction_code: result.transaction_code,
            raw_status: result.raw_status,
            reader_status: result.reader_status,
            reader_state: result.reader_state,
            entry_mode: result.entry_mode,
            brand: result.brand,
            masked_number_last4: result.masked_number_last4,
            auth_code: result.auth_code,
            receipt_url: result.receipt_url,
          },
        };
      }

      if (result.normalized_status === 'cancelled') {
        throw new Error(result.message || 'Kartenzahlung am SumUp Reader wurde abgebrochen.');
      }

      throw new Error(result.message || 'SumUp Kartenzahlung fehlgeschlagen.');
    }

    throw new Error('SumUp hat nicht rechtzeitig geantwortet.');
  };

  const handlePayment = async (paymentMethod, tip = 0, internalBookingType = null, paymentContext = null) => {
    if (items.length === 0) { toast.error('Warenkorb ist leer'); return; }
    if (!activeOrderId && (discountAmount > 0 || discountPercent > 0) && discountReason.trim().length < 3) {
      toast.error('Bitte einen Rabatt-Grund eingeben');
      return;
    }
    setIsProcessing(true);
    const splitSelection = paymentContext?.splitSelection || {
      mode: 'shared',
      items,
      selections: [],
      total: finalTotal,
      selectedCount: 0,
      isAllSelected: true,
    };
    const isSeparateSelection = splitSelection.mode === 'separate' && splitSelection.selectedCount > 0;
    const isPartialSplit = Boolean(activeOrderId && isSeparateSelection && !splitSelection.isAllSelected);
    const payingItems = isSeparateSelection ? splitSelection.items : items;
    const payableTotal = isSeparateSelection ? splitSelection.total : finalTotal;
    const requiresGpTom = paymentMethod === 'card' && paymentContext?.provider === 'gptom';
    const requiresSumUp = isSumUpMethodId(paymentMethod) && paymentContext?.provider === 'sumup';
    const manualCardPayment = paymentMethod === 'card_manual';
    const bankTransferPayment = paymentMethod === 'bank_transfer';
    const customerCardPayment = paymentMethod === 'customer_card';
    const internalPayment = paymentMethod === 'internal';

    const mergePaymentDetails = (details = null) => {
      const merged = {
        ...(details || {}),
      };

      if (paymentContext?.provider && !merged.payment_provider) {
        merged.payment_provider = paymentContext.provider;
      }

      const metadata = {
        ...(details?.payment_metadata || {}),
        ...(paymentContext?.paymentMetadata || {}),
      };
      if (Object.keys(metadata).length > 0) {
        merged.payment_metadata = metadata;
      }

      return Object.keys(merged).length > 0 ? merged : null;
    };

    try {
      let completed;
      let splitResult = null;

      if (isOnline) {
        if (isPartialSplit) {
          splitResult = await splitOrderForPayment(activeOrderId, splitSelection.selections);
          const splitOrder = splitResult?.split_order;
          if (!splitOrder?.id) {
            throw new Error('Teilzahlung konnte nicht vorbereitet werden.');
          }

          let paymentDetails = null;
          if (requiresGpTom) {
            paymentDetails = await runGpTomCardPayment({
              amount: payableTotal,
              tip,
              reference: splitOrder.order_number || splitOrder.id,
              onStatusChange: paymentContext?.onStatusChange,
            });
          } else if (requiresSumUp) {
            paymentDetails = await runSumUpCardPayment({
              amount: payableTotal,
              tip,
              reference: splitOrder.order_number || splitOrder.id,
              onStatusChange: paymentContext?.onStatusChange,
            });
          } else if (manualCardPayment) {
            paymentDetails = mergePaymentDetails(buildManualCardPaymentDetails());
          } else if (bankTransferPayment) {
            paymentDetails = mergePaymentDetails({
              payment_provider: 'bank_transfer',
              payment_reference: '',
              payment_metadata: { booking_type: 'bank_transfer' },
            });
          } else if (customerCardPayment) {
            paymentDetails = mergePaymentDetails({
              payment_provider: 'customer_card',
              payment_reference: '',
              payment_metadata: { booking_type: 'customer_card' },
            });
          } else if (internalPayment) {
            paymentDetails = mergePaymentDetails({
              payment_provider: 'internal',
              payment_reference: '',
              payment_metadata: { internal_booking_type: internalBookingType || 'eigenverbrauch' },
            });
          }

          completed = await completeOrder(splitOrder.id, paymentMethod, null, tip, internalBookingType, paymentDetails);

          if (splitResult?.original_order) {
            loadOrder(splitResult.original_order);
            setDiscountPercent(splitResult.original_order.discount_percent || 0);
            setDiscountAmount(splitResult.original_order.discount_amount || 0);
          }
        } else {
          let orderId = activeOrderId;
          let orderReference = activeOrderId || `POS-${Date.now()}`;
          if (!orderId) {
            const order = await createOrder({
              items: payingItems,
              table_id: selectedTable?.id ?? null,
              table_name: selectedTable?.name ?? null,
              discount_amount: discountAmount,
              discount_percent: discountPercent,
              discount_reason: discountPercent > 0 || discountAmount > 0 ? discountReason : '',
              cashier_id: user?.id || '',
              notes: tip > 0 ? `Trinkgeld: €${tip.toFixed(2)}` : '',
              fulfillment_type: fulfillmentType,
              order_source: 'pos',
            });
            orderId = order.id;
            orderReference = order.order_number || order.id;
          }

          let paymentDetails = null;
          if (requiresGpTom) {
            paymentDetails = await runGpTomCardPayment({
              amount: payableTotal,
              tip,
              reference: orderReference,
              onStatusChange: paymentContext?.onStatusChange,
            });
          } else if (requiresSumUp) {
            paymentDetails = await runSumUpCardPayment({
              amount: payableTotal,
              tip,
              reference: orderReference,
              onStatusChange: paymentContext?.onStatusChange,
            });
          } else if (manualCardPayment) {
            paymentDetails = mergePaymentDetails(buildManualCardPaymentDetails());
          } else if (bankTransferPayment) {
            paymentDetails = mergePaymentDetails({
              payment_provider: 'bank_transfer',
              payment_reference: '',
              payment_metadata: { booking_type: 'bank_transfer' },
            });
          } else if (customerCardPayment) {
            paymentDetails = mergePaymentDetails({
              payment_provider: 'customer_card',
              payment_reference: '',
              payment_metadata: { booking_type: 'customer_card' },
            });
          } else if (internalPayment) {
            paymentDetails = mergePaymentDetails({
              payment_provider: 'internal',
              payment_reference: '',
              payment_metadata: { internal_booking_type: internalBookingType || 'eigenverbrauch' },
            });
          }

          completed = await completeOrder(orderId, paymentMethod, null, tip, internalBookingType, paymentDetails);
        }
      } else {
        if (requiresGpTom || requiresSumUp) {
          throw new Error(requiresGpTom ? 'GP tom ist offline nicht verfuegbar.' : 'SumUp ist offline nicht verfuegbar.');
        }
        if (isPartialSplit || isSeparateSelection) {
          throw new Error('Getrennt zahlen ist offline nicht verfuegbar.');
        }

        const orderData = {
          items: payingItems,
          discount_amount: discountAmount,
          discount_percent: discountPercent,
          discount_reason: discountPercent > 0 || discountAmount > 0 ? discountReason : '',
          cashier_id: user?.id || '',
          notes: tip > 0 ? `Trinkgeld: €${tip.toFixed(2)}` : '',
        };
        completed = {
          ...orderData,
          id: `offline-${Date.now()}`,
          order_number: `OFF-${Date.now()}`,
          payment_method: paymentMethod,
          payment_provider: manualCardPayment
            ? 'manual_terminal'
            : bankTransferPayment
              ? 'bank_transfer'
              : customerCardPayment
                ? 'customer_card'
              : internalPayment
                ? 'internal'
                : '',
          payment_metadata: manualCardPayment
            ? { terminal_type: 'manual' }
            : customerCardPayment
              ? { booking_type: 'customer_card' }
            : internalPayment
              ? { internal_booking_type: internalBookingType || 'eigenverbrauch' }
              : {},
          total: payableTotal,
          status: 'completed',
          created_at: new Date().toISOString(),
          completed_at: new Date().toISOString(),
          is_synced: false,
        };
        await savePendingOrder(completed);
        toast.info('Offline gespeichert');
      }

      setCompletedOrder(completed);
      showPrintWarnings(completed?.print_jobs, 'Beleg konnte nicht automatisch gedruckt werden.');
      if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
      setShowReceipt(paymentContext?.receiptEnabled ?? autoOpenReceipt);
      setShowMobileCart(false);
      if (!isPartialSplit) {
        clearCart();
        setDiscountPercent(0);
        setDiscountAmount(0);
      }
      setTipAmount(0);
      toast.success('Bezahlt!');
    } catch (error) {
      const message = getApiErrorMessage(error, 'Fehler beim Bezahlen');
      if (!(requiresGpTom || requiresSumUp)) {
        toast.error(message);
      }
      throw error instanceof Error ? error : new Error(message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Only option groups explicitly assigned to this product apply — previously EVERY
  // group showed up on EVERY product, which meant e.g. "Wähle deine Pastasauce" would
  // pop up when adding a Coke.
  const getRelevantOptionGroups = (product) =>
    optionGroups.filter((group) => (group.product_ids || []).includes(product?.id));

  const handleAddProduct = (product) => {
    const relevantGroups = getRelevantOptionGroups(product);
    if (relevantGroups.length > 0) {
      blurActiveElement();
      setModifierProduct(product);
      const preselected = {};
      relevantGroups.forEach((group) => {
        const preselectedOptionIds = (group.article_options || [])
          .filter((opt) => opt.preselected)
          .map((opt) => opt.id);
        if (preselectedOptionIds.length === 0) return;
        preselected[group.id] = group.max_selection === 1 ? preselectedOptionIds[0] : preselectedOptionIds;
      });
      setSelectedModifiers(preselected);
    } else {
      addItem(product, [], {
        course: serviceCourse,
        sendImmediately: serviceCourse === 'Sofort',
      });
      if (navigator.vibrate) navigator.vibrate(30);
      toast.success(product.name, { duration: 600 });
    }
  };

  const confirmModifiers = () => {
    if (!modifierProduct) return;
    const relevantGroups = getRelevantOptionGroups(modifierProduct);
    const missingRequired = relevantGroups.find((group) => {
      const min = group.min_selection || 0;
      if (min === 0) return false;
      const sel = selectedModifiers[group.id];
      const count = Array.isArray(sel) ? sel.length : (sel ? 1 : 0);
      return count < min;
    });
    if (missingRequired) {
      toast.error(`Bitte "${missingRequired.name}" auswählen`);
      return;
    }

    const modifiers = [];
    relevantGroups.forEach(group => {
      const sel = selectedModifiers[group.id];
      if (!sel) return;
      const ids = Array.isArray(sel) ? sel : [sel];
      ids.forEach(optId => {
        const opt = (group.article_options || []).find(o => o.id === optId);
        if (opt) modifiers.push({ name: opt.name, price_delta: opt.price_delta || 0 });
      });
    });
    addItem(modifierProduct, modifiers, {
      course: serviceCourse,
      sendImmediately: serviceCourse === 'Sofort',
    });
    if (navigator.vibrate) navigator.vibrate(30);
    toast.success(modifierProduct.name, { duration: 600 });
    setModifierProduct(null);
    setSelectedModifiers({});
  };

  const toggleModifier = (group, optId) => {
    const isMultiSelect = group.max_selection !== 1;
    setSelectedModifiers(prev => {
      if (isMultiSelect) {
        const current = prev[group.id] || [];
        const next = current.includes(optId)
          ? current.filter(id => id !== optId)
          : [...current, optId];
        return { ...prev, [group.id]: next };
      }
      // max_selection === 1: toggle off if same
      return { ...prev, [group.id]: prev[group.id] === optId ? null : optId };
    });
  };

  const renderCartItem = (item, isMobile = false) => {
    const key = item.cartKey || item.product_id;
    const visual = getCartItemVisual(item);
    const Icon = visual.icon;
    const CourseIcon = visual.courseIcon;

    // Keep the swipe shortcut, but never make core POS actions discoverable only by a gesture.
    const canSwipe = item.isSent;

    return (
      <div key={key} className={isMobile ? 'border-b border-[#d7dcde] px-2 py-1' : 'border-b border-[#d7dcde] px-2.5 py-1.5'}>
        <SwipeReveal
          disabled={!canSwipe}
          actions={({ close }) => (
            <>
              <button
                type="button"
                onClick={close}
                className="flex flex-1 items-center justify-center bg-slate-500 text-xs font-semibold uppercase tracking-wide text-white active:bg-slate-600"
              >
                Schließen
              </button>
              <button
                type="button"
                onClick={() => { setRabattDialog({ item }); close(); }}
                className="flex flex-1 items-center justify-center bg-amber-600 text-xs font-semibold uppercase tracking-wide text-white active:bg-amber-700"
              >
                Rabatt
              </button>
              <button
                type="button"
                onClick={() => { handleRemoveOrVoidItem(item); close(); }}
                className="flex flex-1 items-center justify-center bg-rose-600 text-xs font-semibold uppercase tracking-wide text-white active:bg-rose-700"
              >
                Stornieren
              </button>
            </>
          )}
        >
        <div className={`flex items-start ${isMobile ? 'gap-1.5' : 'gap-2'}`}>
          <div
            className={`mt-0.5 flex shrink-0 items-center justify-center text-[#3b4950] ${isMobile ? 'h-5 w-5' : 'h-7 w-7'}`}
          >
            <Icon className={isMobile ? 'h-3.5 w-3.5' : 'h-4 w-4'} strokeWidth={2.1} />
          </div>

          <div className="min-w-0 flex-1">
            <div className={`flex items-start justify-between ${isMobile ? 'gap-1.5' : 'gap-2'}`}>
              <div className="min-w-0 flex-1">
                <div className={`flex flex-wrap items-center ${isMobile ? 'gap-1' : 'gap-1.5'}`}>
                  {visual.showCourse ? (
                    <span
                      className={`inline-flex items-center gap-1 font-semibold uppercase text-[#526068] ${
                        isMobile ? 'text-[7px] tracking-[0.07em]' : 'text-[8px] tracking-[0.08em]'
                      }`}
                    >
                      <CourseIcon className={isMobile ? 'h-2 w-2' : 'h-2.5 w-2.5'} strokeWidth={2.1} />
                      {visual.courseLabel}
                    </span>
                  ) : null}

                  {item.isSent ? (
                    <span className={`inline-flex font-semibold uppercase text-[#526068] ${
                      isMobile ? 'text-[7px] tracking-[0.07em]' : 'text-[8px] tracking-[0.08em]'
                    }`}>
                      Gesendet
                    </span>
                  ) : null}
                  {item.course && item.course !== 'Sofort' ? (
                    <span className={`inline-flex font-semibold uppercase text-[#526068] ${
                      isMobile ? 'text-[7px] tracking-[0.07em]' : 'text-[8px] tracking-[0.08em]'
                    }`}>
                      {item.course}
                    </span>
                  ) : null}
                </div>

                <p className={`min-w-0 truncate font-semibold leading-tight text-[#24323b] ${isMobile ? 'mt-0 text-[12px]' : 'mt-px text-[13px]'}`}>
                  {item.quantity}x {item.product_name}
                </p>

                <div className={`mt-0 flex flex-wrap items-center gap-x-1 gap-y-0 text-muted-foreground ${isMobile ? 'text-[8px]' : 'text-[9px]'}`}>
                  <span>€{item.price.toFixed(2)} / Stk</span>
                  {visual.categoryName ? <span>{visual.categoryName}</span> : null}
                  {item.modifiers && item.modifiers.length > 0 ? (
                    <span className="truncate">{item.modifiers.map((modifier) => modifier.name).join(', ')}</span>
                  ) : null}
                </div>
              </div>

              <div className="flex shrink-0 flex-col items-end gap-0.5">
                <span className={`font-bold tabular-nums text-[#24323b] ${isMobile ? 'text-[12px]' : 'text-[13px]'}`}>
                  €{item.total.toFixed(2)}
                </span>
                <div className="flex items-center gap-0">
                  <button
                    type="button"
                    onClick={() => (
                      item.isSent && item.quantity <= 1
                        ? handleRemoveOrVoidItem(item)
                        : updateQuantity(key, -1)
                    )}
                    disabled={item.isSent}
                    title={item.isSent ? 'Bereits gesendet - nur Storno erlaubt' : 'Menge verringern'}
                    className="touch-manipulation flex h-4 w-4 items-center justify-center text-[#314047] active:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-35"
                  >
                    <Minus className="h-2.5 w-2.5" />
                  </button>
                  <span className="min-w-[0.9rem] text-center text-[10px] font-semibold tabular-nums text-[#24323b]">
                    {item.quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => updateQuantity(key, 1)}
                    title={item.isSent ? 'Neue Position zur Bestellung hinzufügen' : 'Menge erhöhen'}
                    className="touch-manipulation flex h-4 w-4 items-center justify-center text-[#314047] active:bg-slate-100"
                  >
                    <Plus className="h-2.5 w-2.5" />
                  </button>
                  {item.isSent ? (
                    <button
                      type="button"
                      onClick={() => setRabattDialog({ item })}
                      title="Rabatt auf diese Position"
                      className="touch-manipulation ml-0.5 flex h-4 w-4 items-center justify-center text-[#526068] active:bg-slate-100"
                    >
                      <Percent className="h-2.5 w-2.5" />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => handleRemoveOrVoidItem(item)}
                    title={item.isSent ? 'Position stornieren' : 'Position entfernen'}
                    className="touch-manipulation ml-0.5 flex h-4 w-4 items-center justify-center text-[#526068] active:bg-slate-100"
                  >
                    <Trash2 className="h-2.5 w-2.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        </SwipeReveal>
      </div>
    );
  };

  // ── Cart Content ────────────────────────────────────────────────────────────
  const CartContent = ({ isMobile = false }) => (
    <div className="flex h-full min-h-0 flex-col">
      <div
        data-vaul-no-drag={isMobile ? '' : undefined}
        className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${isMobile ? 'touch-pan-y px-0 py-0' : 'px-0 py-1'}`}
      >
        {items.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-4 py-8 text-center text-muted-foreground">
            <ClipboardList className="mb-3 h-14 w-14 text-slate-300" />
            <p className="text-sm leading-relaxed">
              Keine Artikel hinzugefügt.<br />Bitte zuerst auswählen oder scannen.
            </p>
          </div>
        ) : (
          <div className="bg-card">
            {items.map((item) => renderCartItem(item, isMobile))}
          </div>
        )}
      </div>

      {/* Cart Footer */}
      <div
        className={`border-t bg-card ${isMobile ? 'space-y-1 px-2 py-1.5 pb-2.5' : 'space-y-1.5 px-3 py-2.5'}`}
        style={{ borderColor: posBorderColor }}
      >
        {(discountPercent > 0 || discountAmount > 0) && (
          <div className={`flex justify-between text-[#526068] ${isMobile ? 'text-[11px]' : 'text-sm'}`}>
            <span>Rabatt {discountPercent > 0 ? `(${discountPercent}%)` : ''}</span>
            <span>-€{(total - finalTotal).toFixed(2)}</span>
          </div>
        )}

        <div className={`flex justify-between font-bold text-[#24323b] ${isMobile ? 'text-[17px]' : 'text-lg'}`}>
          <span>Gesamt</span>
          <span className="tabular-nums" style={{ color: posHeaderColor }}>€{finalTotal.toFixed(2)}</span>
        </div>

        <div className={`flex ${isMobile ? 'gap-1' : 'gap-2'}`}>
          {hasPerm('rabatt_geben') && (
            <button
              type="button"
              onClick={() => openOverlay(() => setShowDiscount(true))}
              disabled={items.length === 0}
              className={`touch-manipulation flex items-center justify-center border bg-muted text-[#314047] active:bg-border disabled:opacity-40 shrink-0 ${isMobile ? 'h-11 w-11' : 'h-11 w-10'}`}
              style={{ borderColor: posBorderColor }}
              title="Rabatt"
            >
              <Percent className={isMobile ? 'h-[18px] w-[18px]' : 'w-5 h-5'} />
            </button>
          )}
          {activeOrderId && (
            <button
              type="button"
              onClick={() => openOverlay(() => openPaymentDialog(null, 'split'))}
              disabled={isProcessing || items.length === 0}
              className={`touch-manipulation flex items-center justify-center border bg-muted text-[#314047] active:bg-border disabled:opacity-40 shrink-0 ${isMobile ? 'h-11 w-11' : 'h-11 px-3 text-xs font-semibold'}`}
              style={{ borderColor: posBorderColor }}
              title="Positionen getrennt bezahlen"
            >
              <span className={isMobile ? 'text-base leading-none' : ''}>{isMobile ? '÷' : 'Split'}</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => { if (items.length > 0) handleSendToKitchen(); }}
            disabled={isProcessing || items.length === 0}
            className={`touch-manipulation flex flex-1 items-center justify-center gap-1.5 border bg-[#eef2f5] text-[#314047] active:bg-[#dfe7ec] disabled:opacity-50 font-semibold ${isMobile ? 'h-11 text-[13px]' : 'h-11 text-sm'}`}
            style={{ borderColor: posBorderColor }}
          >
            {isProcessing ? <Loader2 className={`${isMobile ? 'h-[18px] w-[18px]' : 'w-5 h-5'} animate-spin`} /> : (
              <><ClipboardList className={isMobile ? 'h-[18px] w-[18px]' : 'w-5 h-5'} />Bestellen</>
            )}
          </button>
          {canTakePayment && (
            <button
              type="button"
              onClick={() => { if (items.length > 0) openOverlay(() => openPaymentDialog(null, 'shared')); }}
              disabled={isProcessing || items.length === 0 || !canTakePayment}
              className={`touch-manipulation flex flex-1 items-center justify-center gap-1.5 border border-emerald-700 bg-emerald-600 text-white active:bg-emerald-700 disabled:opacity-50 font-semibold ${isMobile ? 'h-11 text-[13px]' : 'h-11 text-sm'}`}
              data-testid="pay-btn"
            >
              {isProcessing ? <Loader2 className={`${isMobile ? 'h-[18px] w-[18px]' : 'w-5 h-5'} animate-spin`} /> : (
                <><CreditCard className={isMobile ? 'h-[18px] w-[18px]' : 'w-5 h-5'} />Bezahlen</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-300" />
      </div>
    );
  }

  return (
    <div
      className="flex h-full flex-col text-[#24323b]"
      style={{ backgroundColor: posCanvasColor }}
      data-testid="pos-terminal"
    >
      {/* ── Main content row ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Category quick-select rail (desktop/tablet — replaces horizontal tabs above md) */}
        <div
          className="hidden flex-shrink-0 flex-col overflow-y-auto border-r md:flex md:w-32 lg:w-36 xl:w-40"
          style={{ backgroundColor: posRailColor, borderColor: posBorderColor }}
        >
          <button
            onClick={() => setSelectedCategory(null)}
            className="flex items-center gap-2 border-b px-2 py-2.5 text-left transition-colors"
            style={{
              backgroundColor: !selectedCategory ? posHeaderColor : posRailColor,
              color: !selectedCategory ? '#ffffff' : '#24323b',
              borderColor: posBorderColor,
            }}
            data-testid="cat-rail-all"
          >
            <span
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center text-white"
              style={{ backgroundColor: !selectedCategory ? 'rgba(255,255,255,0.12)' : posAccentColor }}
            >
              ✦
            </span>
            <span className="truncate text-[13px] font-semibold leading-tight">Alle</span>
          </button>
          {visibleCategories.map((cat) => {
            const tone = getVisual(cat);
            const isActive = selectedCategory === cat.id;
            const Icon = tone.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                className="flex items-center gap-2 border-b px-2 py-2.5 text-left transition-colors hover:bg-white/60"
                style={{
                  backgroundColor: isActive ? posHeaderColor : tone.softer,
                  color: isActive ? 'white' : '#24323b',
                  borderColor: posBorderColor,
                  borderLeft: `4px solid ${tone.base}`,
                }}
                data-testid={`cat-rail-${cat.id}`}
              >
                <span
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center text-white"
                  style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.12)' : tone.iconBg }}
                >
                  <Icon className="h-4 w-4" strokeWidth={2} />
                </span>
                <span className="line-clamp-2 text-[12px] font-semibold leading-tight">{cat.name}</span>
              </button>
            );
          })}
        </div>

        {/* Products panel */}
        <div className="flex flex-1 flex-col overflow-hidden" style={{ backgroundColor: posCanvasColor }}>
          <div className="border-b px-2 py-1.5 md:px-3 md:py-2.5" style={{ backgroundColor: posHeaderColor, borderColor: posBorderColor }}>
            <div className="md:hidden">
              <div className="flex h-9 items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => openOverlay(() => setShowTablePicker(true))}
                  className={`inline-flex h-full items-center gap-1.5 border border-white/15 px-2 text-left text-[11px] font-semibold text-white transition-all ${
                    mobileSearchOpen ? 'w-9 justify-center px-0' : 'min-w-0 flex-1'
                  }`}
                  style={{ backgroundColor: posSummaryColor }}
                  data-testid="quick-table-select"
                  title={selectedTable ? `${selectedTable.name} · Tisch wechseln` : 'Tisch wählen'}
                >
                  <UtensilsCrossed className="h-3.5 w-3.5 shrink-0" />
                  {!mobileSearchOpen ? (
                    <span className="truncate">{selectedTable ? selectedTable.name : 'Tisch wählen'}</span>
                  ) : null}
                </button>

                {mobileSearchOpen ? (
                  <div className="relative min-w-0 flex-1">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      autoFocus
                      value={productSearch}
                      onChange={(event) => setProductSearch(event.target.value)}
                      placeholder="Artikel suchen"
                      className="h-9 rounded-none border-[#d0c9bd] bg-card pl-7 pr-7 text-[12px] text-[#24323b] placeholder:text-muted-foreground"
                    />
                    <button
                      type="button"
                      onClick={() => { setProductSearch(''); setMobileSearchOpen(false); }}
                      className="absolute right-0 top-0 inline-flex h-9 w-8 items-center justify-center text-muted-foreground"
                      aria-label="Suche schließen"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setMobileSearchOpen(true)}
                    className="inline-flex h-9 w-9 items-center justify-center border border-white/15 bg-white/10 text-white"
                    aria-label="Artikel suchen"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="hidden md:block">
              <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-lg font-bold tracking-tight text-white">
                      {selectedTable?.name || 'Direktverkauf'}
                    </h1>
                    <span className="border border-slate-700 bg-[#101722] px-2 py-0.5 text-xs font-semibold text-slate-300">
                      {activeOrderId ? 'Offene Bestellung aktiv' : 'Neue Bestellung'}
                    </span>
                    {selectedCategoryEntry ? (
                      <span className="inline-flex items-center gap-1 border border-white/20 bg-white/10 px-2 py-0.5 text-xs font-semibold text-white">
                        {(() => {
                          const Icon = selectedCategoryTone.icon;
                          return <Icon className="h-3.5 w-3.5" style={{ color: posAccentColor }} strokeWidth={2.1} />;
                        })()}
                        {selectedCategoryEntry.name}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {itemCount} Artikel im Checkout · {fulfillmentLabels[fulfillmentType] || 'Dine-in'} · {activeOrderId ? 'Kitchen sync aktiv' : 'Noch nicht gesendet'}
                  </p>
                </div>

                <div className="sm:flex">
                  <div className="border border-white/10 bg-white/10 px-3 py-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/60">Artikel</p>
                    <p className="text-lg font-bold text-white">{itemCount}</p>
                  </div>
                </div>
              </div>

              <div className="mt-2 flex flex-col gap-2 xl:flex-row xl:items-center">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={productSearch}
                    onChange={(event) => setProductSearch(event.target.value)}
                    placeholder="Artikel suchen, Barcode scannen oder Stichwort eingeben"
                    className="h-10 rounded-none border-[#d0c9bd] bg-card pl-10 pr-10 text-[#24323b] placeholder:text-muted-foreground"
                  />
                  {hasProductSearch ? (
                    <button
                      type="button"
                      onClick={() => setProductSearch('')}
                      className="absolute right-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-[#24323b]"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={() => openOverlay(() => setShowTablePicker(true))}
                  className="inline-flex items-center gap-2 border border-white/10 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:brightness-110 xl:self-stretch"
                  style={{ backgroundColor: posSummaryColor }}
                  data-testid="quick-table-select"
                >
                  <UtensilsCrossed className="h-3.5 w-3.5" />
                  {selectedTable ? `${selectedTable.name} · Tisch wechseln` : 'Tisch wählen'}
                </button>
              </div>
            </div>
          </div>

          <div className="border-b bg-card px-2 py-1 md:px-3 md:py-2" style={{ borderColor: posBorderColor }}>
            <div className="flex items-center gap-1 overflow-x-auto md:gap-2">
              <span className="shrink-0 text-[8px] font-semibold uppercase tracking-[0.1em] text-muted-foreground md:text-[10px] md:tracking-[0.16em]">Ausgabe</span>
              {['Sofort', 'Gang 1', 'Gang 2', 'Gang 3'].map((course) => {
                const isActive = serviceCourse === course;
                return (
                  <button
                    key={course}
                    type="button"
                    onClick={() => setServiceCourse(course)}
                    className="shrink-0 border px-1.5 py-0.5 text-[9px] font-semibold transition-colors md:px-3 md:py-1.5 md:text-xs"
                    style={{
                      backgroundColor: isActive ? posHeaderColor : '#ffffff',
                      color: isActive ? '#ffffff' : '#475569',
                      borderColor: isActive ? posAccentColor : posBorderColor,
                    }}
                    aria-pressed={isActive}
                  >
                    <span className="md:hidden">{course === 'Sofort' ? 'Sofort' : course.replace('Gang ', 'G')}</span>
                    <span className="hidden md:inline">{course}</span>
                  </button>
                );
              })}
              <span className="ml-auto hidden shrink-0 text-[11px] text-muted-foreground lg:inline">
                Neue Artikel werden {serviceCourse === 'Sofort' ? 'sofort gesendet' : `für ${serviceCourse} vorgemerkt`}.
              </span>
            </div>
          </div>

          {scopedSalesMenus.length > 1 ? (
            <div className="border-b px-2 py-1.5 md:px-3 md:py-2" style={{ backgroundColor: posRailColor, borderColor: posBorderColor }}>
              <div className="flex flex-wrap gap-1">
                {scopedSalesMenus.map((menu) => (
                  <button
                    key={menu.id}
                    type="button"
                    onClick={() => setActiveSalesMenuId(menu.id)}
                    className="px-2.5 py-1 text-[11px] font-semibold transition-colors md:px-3 md:py-1.5 md:text-xs"
                    style={{
                      backgroundColor: activeSalesMenuId === menu.id ? posHeaderColor : '#ffffff',
                      color: activeSalesMenuId === menu.id ? '#ffffff' : '#475569',
                      border: `1px solid ${activeSalesMenuId === menu.id ? posAccentColor : posBorderColor}`,
                    }}
                  >
                    {menu.name}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {/* Category tabs — horizontal (mobile/tablet only, replaced by left rail on md+) */}
          <div
            className="hide-scrollbar flex-shrink-0 overflow-x-auto border-b px-2 py-1 md:hidden"
            style={{ backgroundColor: posRailColor, borderColor: posBorderColor }}
          >
            <div className="flex min-w-max gap-1">
              <button
                onClick={() => setSelectedCategory(null)}
                className="px-2 py-1 text-[11px] font-semibold whitespace-nowrap transition-colors"
                style={{
                  backgroundColor: !selectedCategory ? posHeaderColor : '#ffffff',
                  color: !selectedCategory ? '#ffffff' : '#475569',
                  border: `1px solid ${!selectedCategory ? posAccentColor : posBorderColor}`,
                }}
              >
                <span className="mr-0.5">✦</span>Alle
              </button>
              {visibleCategories.map((cat) => {
                const tone = getVisual(cat);
                const Icon = tone.icon;
                return (
                  <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold whitespace-nowrap transition-colors"
                  style={{
                    backgroundColor: selectedCategory === cat.id ? posHeaderColor : tone.softer,
                    color: selectedCategory === cat.id ? 'white' : '#24323b',
                    border: `1px solid ${selectedCategory === cat.id ? posAccentColor : tone.borderStrong}`,
                  }}
                  data-testid={`cat-${cat.id}`}
                >
                  <span
                    className="inline-flex h-4 w-4 items-center justify-center"
                    style={{ color: selectedCategory === cat.id ? '#ffffff' : tone.base }}
                  >
                    <Icon className="h-3.5 w-3.5" strokeWidth={2.1} />
                  </span>
                  {cat.name}
                </button>
                );
              })}
            </div>
          </div>

          {/* Product / category grid */}
          <div className="h-[31dvh] flex-none overflow-auto p-1.5 lg:h-auto lg:flex-1 md:p-3" style={{ backgroundColor: posCanvasColor }}>
            {/* If no category or search selected: show category tiles */}
            {!selectedCategory && !hasProductSearch ? (
              <div className="space-y-3 md:space-y-4">
                {hasHomeFavorites ? (
                  <section className="space-y-2.5 md:space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex h-8 w-8 items-center justify-center text-white md:h-9 md:w-9" style={{ backgroundColor: posAccentColor }}>
                        <Sparkles className="h-3.5 w-3.5 md:h-4 md:w-4" strokeWidth={2.2} />
                      </span>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground md:text-sm md:tracking-[0.18em]">Hauptscreen</p>
                        <p className="text-[11px] text-muted-foreground md:text-sm">Gespeicherte Kategorien und Produkte fuer den Schnellzugriff.</p>
                      </div>
                    </div>

                    {featuredCategoriesOnHome.length > 0 ? (
                      <div className={catalogGridClass}>
                        {featuredCategoriesOnHome.map((cat) => (
                          <CategoryFeatureCard
                            key={`featured-category-${cat.id}`}
                            category={cat}
                            tone={getVisual(cat)}
                            subtitle="Kategorie"
                            onClick={() => setSelectedCategory(cat.id)}
                          />
                        ))}
                      </div>
                    ) : null}

                    {featuredProductsOnHome.length > 0 ? (
                      <div className={catalogGridClass}>
                        {featuredProductsOnHome.map((product) => {
                          const cat = resolveCategoryForProduct(product);
                          return (
                            <ProductFeatureCard
                              key={`featured-product-${product.__menu_key || product.id}`}
                              product={product}
                              visual={getProductVisual(product, cat)}
                              onClick={() => handleAddProduct(product)}
                            />
                          );
                        })}
                      </div>
                    ) : null}
                  </section>
                ) : null}

                <section className="space-y-2.5 md:space-y-3">
                  {hasHomeFavorites ? (
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground md:text-sm md:tracking-[0.18em]">Alle Kategorien</p>
                      <p className="text-[11px] text-muted-foreground md:text-sm">Dieselbe Reihenfolge wie im linken Bedienstreifen.</p>
                    </div>
                  ) : null}

                  <div className={catalogGridClass}>
                    {visibleCategories.map((cat) => (
                      <CategoryFeatureCard
                        key={cat.id}
                        category={cat}
                        tone={getVisual(cat)}
                        onClick={() => setSelectedCategory(cat.id)}
                      />
                    ))}
                  </div>
                </section>

                {filteredProducts.filter((product) => !product.category_id).length > 0 ? (
                  <section className="space-y-2.5 md:space-y-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground md:text-sm md:tracking-[0.18em]">Direktartikel</p>
                      <p className="text-[11px] text-muted-foreground md:text-sm">Artikel ohne Kategorie bleiben direkt auf dem Startscreen verfuegbar.</p>
                    </div>
                    <div className={catalogGridClass}>
                      {filteredProducts.filter((product) => !product.category_id).map((product) => (
                        <ProductFeatureCard
                          key={product.__menu_key || product.id}
                          product={product}
                          visual={getProductVisual(product, null)}
                          onClick={() => handleAddProduct(product)}
                        />
                      ))}
                    </div>
                  </section>
                ) : null}
              </div>
            ) : (
              /* Category selected or search active: show products */
              <>
                <div className="mb-1.5 flex flex-col gap-1.5 sm:mb-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {canReturnToCatalog ? (
                      <button
                      type="button"
                      onClick={handleBackToCatalog}
                      className="inline-flex items-center gap-1 border bg-card px-2 py-1 text-[10px] font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-[#24323b] sm:px-2.5 sm:py-1.5 sm:text-[11px]"
                      style={{ borderColor: posBorderColor }}
                    >
                      <ChevronLeft className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                      {selectedCategoryEntry ? `Zurück zu ${backTargetLabel}` : 'Zurück zur Übersicht'}
                      </button>
                    ) : null}

                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground sm:text-[11px] sm:tracking-[0.18em]">
                      {hasProductSearch ? `${filteredProducts.length} Treffer` : `${filteredProducts.length} Artikel`}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {selectedCategoryEntry ? (
                      <span
                        className="inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-semibold sm:px-2 sm:py-1 sm:text-[11px]"
                        style={{
                          backgroundColor: '#ffffff',
                          color: '#24323b',
                          borderColor: selectedCategoryTone?.borderStrong || posBorderColor,
                        }}
                      >
                        {(() => {
                          const Icon = selectedCategoryTone.icon;
                          return <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" strokeWidth={2.1} />;
                        })()}
                        {selectedCategoryEntry.name}
                      </span>
                    ) : null}

                    {hasProductSearch ? (
                      <button
                        type="button"
                        onClick={() => setProductSearch('')}
                        className="border bg-card px-2 py-1 text-[10px] font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-[#24323b] sm:px-2.5 sm:py-1.5 sm:text-[11px]"
                        style={{ borderColor: posBorderColor }}
                      >
                        Suche leeren
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className={catalogGridClass}>
                  {filteredProducts.map((product) => {
                    const cat = resolveCategoryForProduct(product);
                    return (
                      <ProductFeatureCard
                        key={product.__menu_key || product.id}
                        product={product}
                        visual={getProductVisual(product, cat)}
                        onClick={() => handleAddProduct(product)}
                        showCategoryLabel={hasProductSearch}
                      />
                    );
                  })}
                </div>
                {filteredProducts.length === 0 && (
                  <div className="border border-dashed bg-white/70 py-12 text-center text-sm text-muted-foreground" style={{ borderColor: posBorderColor }}>
                    Keine Produkte in dieser Kategorie
                  </div>
                )}
              </>
            )}
          </div>

          {/* Permanent mobile order view: products stay above, the current bon below. */}
          <div className="flex min-h-0 flex-1 flex-col border-t lg:hidden" style={{ backgroundColor: posRailColor, borderColor: posBorderColor }}>
            <div className="flex h-7 flex-shrink-0 items-center justify-between border-b px-2" style={{ backgroundColor: '#ffffff', borderColor: posBorderColor }}>
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#435763]">Bestellschein · {itemCount}</span>
              <button
                type="button"
                onClick={() => setShowDiverseDialog(true)}
                className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase text-[#526068]"
              >
                <Plus className="h-3 w-3" /> Diverse
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <CartContent isMobile />
            </div>
          </div>
        </div>

        {/* Desktop cart panel */}
        <div
          className={`hidden lg:flex ${ORDER_PANEL_WIDTH_CLASSES[orderPanelWidth] || ORDER_PANEL_WIDTH_CLASSES.normal} border-l flex-shrink-0 flex-col`}
          style={{ backgroundColor: posRailColor, borderColor: posBorderColor }}
        >
          <div className="border-b bg-card px-3 py-2 flex-shrink-0" style={{ borderColor: posBorderColor }}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-[#24323b]">Bestellschein</h2>
                {selectedTable && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <UtensilsCrossed className="w-3 h-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{selectedTable.name}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowDiverseDialog(true)}
                  title="Diverse Buchung"
                  className="flex h-8 items-center gap-1 border border-slate-200 bg-slate-50 px-2 text-slate-700 active:bg-slate-100"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span className="text-[10px] font-semibold uppercase tracking-wide">Diverse</span>
                </button>
                {activeOrderId && (
                  <button
                    type="button"
                    onClick={() => setStornoDialog({ item: null })}
                    title="Gesamte Bestellung stornieren"
                    className="flex h-8 items-center gap-1 border border-rose-200 bg-rose-50 px-2 text-rose-600 active:bg-rose-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide">Storno</span>
                  </button>
                )}
                {itemCount > 0 && (
                  <span
                    className="border px-2.5 py-1 text-xs font-semibold text-[#435763]"
                    style={{ backgroundColor: '#f1f5f9', borderColor: posBorderColor }}
                  >
                    {itemCount}
                  </span>
                )}
              </div>
            </div>
          </div>
          <CartContent />
        </div>
      </div>

      {/* Mobile cart drawer */}
      <Drawer open={showMobileCart} onOpenChange={setShowMobileCart}>
        {showMobileCart ? (
          <DrawerContent className="flex h-[92vh] max-h-[92vh] min-h-0 flex-col overflow-hidden rounded-none border-t bg-card">
            <DrawerHeader className="border-b bg-card px-2.5 py-1.5" style={{ borderColor: posBorderColor }}>
              <DrawerTitle className="flex items-center justify-between gap-2 text-sm text-[#24323b]">
                <span>Bestellschein ({itemCount})</span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setShowDiverseDialog(true)}
                    className="flex h-7 items-center gap-1 border border-slate-200 bg-slate-50 px-1.5 text-slate-700"
                  >
                    <Plus className="h-3 w-3" />
                    <span className="text-[9px] font-semibold uppercase tracking-wide">Diverse</span>
                  </button>
                  {activeOrderId && (
                    <button
                      type="button"
                      onClick={() => setStornoDialog({ item: null })}
                      className="flex h-7 items-center gap-1 border border-rose-200 bg-rose-50 px-1.5 text-rose-600"
                    >
                      <Trash2 className="h-3 w-3" />
                      <span className="text-[9px] font-semibold uppercase tracking-wide">Storno</span>
                    </button>
                  )}
                  <button type="button" onClick={() => setShowMobileCart(false)} className="text-muted-foreground">
                    <X className="h-[18px] w-[18px]" />
                  </button>
                </div>
              </DrawerTitle>
            </DrawerHeader>
            <CartContent isMobile />
          </DrawerContent>
        ) : null}
      </Drawer>

      {/* Discount dialog */}
      <Dialog open={showDiscount} onOpenChange={setShowDiscount}>
        <DialogContent className="pos-workflow-dialog max-w-sm gap-0 overflow-hidden rounded-none border p-0">
          <DialogHeader className="pos-dialog-header px-5 py-4"><DialogTitle>Rabatt</DialogTitle></DialogHeader>
          <div className="space-y-4 p-5">
            <div>
              <label className="text-sm font-medium">Prozent (%)</label>
              <Input type="number" min={0} max={100} value={discountPercent}
                onChange={e => { setDiscountPercent(parseFloat(e.target.value) || 0); setDiscountAmount(0); }}
                className="pos-dialog-field h-12 rounded-none text-lg" />
            </div>
            <div className="flex gap-2">
              {[5, 10, 15, 20].map(pct => (
                <button key={pct} onClick={() => { setDiscountPercent(pct); setDiscountAmount(0); }}
                  className={`flex-1 h-12 border font-medium transition-all ${
                    discountPercent === pct ? 'border-primary bg-primary text-primary-foreground' : 'pos-dialog-secondary active:bg-muted'
                  }`}>
                  {pct}%
                </button>
              ))}
            </div>
            <div className="text-center text-muted-foreground text-sm">oder</div>
            <div>
              <label className="text-sm font-medium">Betrag (€)</label>
              <Input type="number" min={0} step={0.5} value={discountAmount}
                onChange={e => { setDiscountAmount(parseFloat(e.target.value) || 0); setDiscountPercent(0); }}
                className="pos-dialog-field h-12 rounded-none text-lg" />
            </div>
            <div>
              <label className="text-sm font-medium">Grund</label>
              <Input value={discountReason} onChange={e => setDiscountReason(e.target.value)}
                placeholder="z. B. Stammgast, Entschuldigung"
                className="pos-dialog-field h-11 rounded-none" />
            </div>
            <Button
              onClick={() => {
                if ((discountPercent > 0 || discountAmount > 0) && discountReason.trim().length < 3) {
                  toast.error('Bitte einen Rabatt-Grund eingeben');
                  return;
                }
                setShowDiscount(false);
              }}
              className="pos-dialog-primary h-12 w-full rounded-none"
            >
              Übernehmen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <StornoDialog
        open={Boolean(stornoDialog)}
        onClose={() => setStornoDialog(null)}
        item={stornoDialog?.item || null}
        onConfirm={handleConfirmStorno}
      />

      <RabattDialog
        open={Boolean(rabattDialog)}
        onClose={() => setRabattDialog(null)}
        item={rabattDialog?.item || null}
        onConfirm={handleConfirmRabatt}
      />

      <DiverseBuchungDialog
        open={showDiverseDialog}
        onClose={() => setShowDiverseDialog(false)}
        categories={categories}
        onConfirm={handleConfirmDiverse}
      />

      <PaymentDialog
        open={showPayDialog}
        onClose={() => { setShowPayDialog(false); setDefaultPayMethod(null); setCheckoutStartMode('shared'); }}
        finalTotal={finalTotal}
        tip={tipAmount}
        setTip={setTipAmount}
        onPay={handlePayment}
        isProcessing={isProcessing}
        defaultMethod={defaultPayMethod}
        paymentMethods={enabledPaymentMethods}
        gpTomConfigured={gpTomConfigured}
        sumUpConfigured={sumUpConfigured}
        isOnline={isOnline}
        items={items}
        allowSplitPayments={Boolean(activeOrderId)}
        initialSplitMode={checkoutStartMode}
        tables={allTables}
        selectedTableId={selectedTable?.id || ''}
        receiptEnabled={autoOpenReceipt}
        onReceiptEnabledChange={setAutoOpenReceipt}
      />

      {/* Receipt dialog */}
      <ReceiptDialog open={showReceipt} onClose={() => setShowReceipt(false)} order={completedOrder} />

      {/* Table quick-pick dialog */}
      <TableQuickPickDialog
        open={showTablePicker}
        onClose={() => setShowTablePicker(false)}
        tables={allTables}
        onSelectTable={handlePickTable}
        onNoTable={handleNoTable}
      />

      {/* Modifier selection dialog */}
      <Dialog open={!!modifierProduct} onOpenChange={() => setModifierProduct(null)}>
        <DialogContent className="pos-workflow-dialog max-w-sm gap-0 overflow-hidden rounded-none border p-0">
          <DialogHeader className="pos-dialog-header px-5 py-4">
            <DialogTitle>{modifierProduct?.name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-4 overflow-auto px-5 pt-4">
            {getRelevantOptionGroups(modifierProduct).map(group => (
              <div key={group.id}>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  {group.name}
                  {(group.min_selection || 0) > 0 && <span className="ml-1 text-red-500">*</span>}
                </p>
                <div className="space-y-1">
                  {(group.article_options || []).map(opt => {
                    const sel = selectedModifiers[group.id];
                    const isSelected = group.max_selection !== 1
                      ? (Array.isArray(sel) && sel.includes(opt.id))
                      : sel === opt.id;
                    return (
                      <button key={opt.id} type="button"
                        onClick={() => toggleModifier(group, opt.id)}
                        className={`w-full flex items-center justify-between border px-3 py-2 text-sm transition-colors ${
                          isSelected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'pos-dialog-secondary hover:border-foreground/40'
                        }`}>
                        <span>{opt.name}</span>
                        {(opt.price_delta || 0) > 0 && (
                          <span className={isSelected ? 'text-emerald-300' : 'text-emerald-600'}>
                            +€{(opt.price_delta).toFixed(2)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 p-5 pt-4">
            <Button variant="outline" className="pos-dialog-secondary flex-1 rounded-none" onClick={() => {
              addItem(modifierProduct, []);
              toast.success(modifierProduct.name, { duration: 600 });
              setModifierProduct(null);
              setSelectedModifiers({});
            }}>
              Ohne Optionen
            </Button>
            <Button className="pos-dialog-primary flex-1 rounded-none" onClick={confirmModifiers}>
              Hinzufügen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
